import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatUserDateTime } from '../../utils/dateFormat';
import { formatSpeed, getFileTimestamp } from '../../utils/timezone';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { formatAssetLabel, formatCommandDeviceLabel as _formatCommandDeviceLabel, formatAuditDeviceLabel, buildDevicesByImei } from '../../utils/deviceLabel';
import { PaginationFooter, paginateArray, usePersistedPageSize } from '../../components/shared/PaginationFooter';
import { MobileSectionMenu } from '../../components/shared/MobileSectionMenu';
import { useUrlPage, useUrlPageSize, useSetUrlParams, useSkipFirstEffect } from '../../components/shared/useUrlPagination';
import { DateRangePresetFilter } from '../../components/shared/DateRangePresetFilter';
import { SyncedTableScroll } from '../../components/admin/SyncedTableScroll';
import { ConfirmActionDialog } from '../../components/shared/ConfirmActionDialog';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/ui/number-input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Terminal, Plus, Send, History, Shield, Eye, Trash2, Pencil, Search,
  Loader2, AlertTriangle, CheckCircle, XCircle, Clock, Zap, FileText,
  ChevronDown, ChevronUp, BarChart3, RefreshCw, Radio, MessageSquare,
  Wifi, WifiOff, Activity, Circle, Upload, Download, X, GripVertical,
  CalendarIcon, Gauge, AlertCircle,
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { format } from 'date-fns';

// ── Constants ──
const PROTOCOLS = ['Teltonika', 'Meitrack'];
const CATEGORIES = ['diagnostics', 'security', 'configuration', 'maintenance', 'custom'];
const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
const BUTTON_COLORS = ['blue', 'red', 'green', 'yellow', 'orange', 'purple', 'gray'];
const DELIVERY_METHODS = [
  { value: 'gprs', label: 'GPRS (TCP Socket)', icon: Radio },
  { value: 'sms', label: 'SMS (SIM Provider API)', icon: MessageSquare },
  { value: 'sms_3rdparty', label: null, icon: Zap }, // label set dynamically based on active provider
];

const RISK_COLORS = {
  low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const BTN_COLOR_MAP = {
  blue: 'bg-blue-600 hover:bg-blue-700 text-white',
  red: 'bg-red-600 hover:bg-red-700 text-white',
  green: 'bg-green-600 hover:bg-green-700 text-white',
  yellow: 'bg-yellow-500 hover:bg-yellow-600 text-black',
  orange: 'bg-orange-500 hover:bg-orange-600 text-white',
  purple: 'bg-purple-600 hover:bg-purple-700 text-white',
  gray: 'bg-gray-500 hover:bg-gray-600 text-white',
};

const CARD_COLOR_MAP = {
  blue: 'bg-blue-100 border-blue-300 dark:bg-blue-900/50 dark:border-blue-700',
  red: 'bg-red-100 border-red-300 dark:bg-red-900/50 dark:border-red-700',
  green: 'bg-green-100 border-green-300 dark:bg-green-900/50 dark:border-green-700',
  yellow: 'bg-yellow-100 border-yellow-300 dark:bg-yellow-900/50 dark:border-yellow-700',
  orange: 'bg-orange-100 border-orange-300 dark:bg-orange-900/50 dark:border-orange-700',
  purple: 'bg-purple-100 border-purple-300 dark:bg-purple-900/50 dark:border-purple-700',
  gray: 'bg-gray-100 border-gray-300 dark:bg-gray-700/50 dark:border-gray-600',
};

const STATUS_BADGE = {
  queued: 'bg-blue-100 text-blue-700',
  sending: 'bg-indigo-100 text-indigo-700',
  sent: 'bg-green-100 text-green-700',
  delivered: 'bg-emerald-200 text-emerald-800',
  acknowledged: 'bg-green-200 text-green-800',
  failed: 'bg-red-100 text-red-700',
  timeout: 'bg-orange-100 text-orange-700',
  expired: 'bg-gray-100 text-gray-600',
  skipped: 'bg-yellow-100 text-yellow-700',
  will_send: 'bg-blue-50 text-blue-600',
};

// ── Default template form ──
const defaultTemplate = {
  name: '', protocol: '', category: 'custom', description: '',
  command_text: '', command_mode: 'single', command_sequence: [],
  risk_level: 'low', button_color: 'blue',
  preferred_delivery_method: 'gprs',
  preconditions: {
    require_online: false, require_speed_zero: false,
    require_ignition_off: false, require_ignition_on: false,
    max_last_seen_minutes: null,
  },
};

const defaultSendPreconditions = {
  require_online: false,
  require_speed_zero: false,
  require_ignition_off: false,
  require_ignition_on: false,
  max_last_seen_minutes: null,
};

// ── Sortable Template Card ──
const SortableTemplateCard = ({ id, children, className = '' }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${className}`}>
      <div className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10" {...attributes} {...listeners}>
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      {children}
    </div>
  );
};

// ── Sortable Protocol Group ──
const SortableProtocolGroup = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10" {...attributes} {...listeners}>
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        {children}
      </div>
    </div>
  );
};

// iter563 — Removed local SyncedTableScroll duplicate. Now imported from
// the shared component below so the no-top-scroll refactor propagates.

const DetailTextBox = ({ children, tone = 'muted', testId }) => {
  const toneClass = tone === 'success'
    ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300'
    : tone === 'danger'
      ? 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300'
      : 'bg-muted text-foreground';
  return (
    <pre
      className={`mt-1 max-h-48 overflow-auto rounded p-2 text-xs font-mono leading-relaxed whitespace-pre-wrap break-all ${toneClass}`}
      data-testid={testId}
    >
      {children}
    </pre>
  );
};
/**
 * iter151 Phase 3.6 — Small presentational card for the audit-log
 * "Status @ Send Time" modal.  Renders one GPS/IO snapshot.  Used by
 * the modal to show "@ Send" + "@ Ack" side-by-side per device.
 *
 * Props:
 *   - `state`   : { found, delta_sec, data: { gps, io_elements, timestamp, protocol, codec } } | null
 *   - `title`   : short header ("@ Send" / "@ Ack")
 *   - `subtitle`: the actual timestamp (ISO or already-formatted)
 *   - `kind`    : 'send' | 'ack' (drives the colour accent)
 *   - `testIdSuffix` : appended to data-testids
 *   - `missingHint`  : optional explanatory text when `state` is null
 */
const DeviceStatePanel = ({ state, title, subtitle, kind, testIdSuffix, missingHint }) => {
  const accent = kind === 'send' ? 'border-blue-200 dark:border-blue-900/40' : 'border-emerald-200 dark:border-emerald-900/40';
  const titleClr = kind === 'send' ? 'text-blue-700 dark:text-blue-300' : 'text-emerald-700 dark:text-emerald-300';
  if (!state) {
    return (
      <div className={`rounded-md border ${accent} bg-muted/20 p-2.5 text-xs`} data-testid={`state-panel-${testIdSuffix}`}>
        <div className="flex items-baseline justify-between mb-1">
          <span className={`font-semibold ${titleClr}`}>{title}</span>
          <span className="text-[10px] text-muted-foreground">{subtitle}</span>
        </div>
        <div className="text-muted-foreground italic">{missingHint || 'No data available.'}</div>
      </div>
    );
  }
  const gps = state.data?.gps || {};
  const lat = gps.latitude;
  const lng = gps.longitude;
  const hasFix = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  const speedKmh = Number.isFinite(gps.speed) ? gps.speed : null;
  const heading = Number.isFinite(gps.angle) ? gps.angle : null;
  const sats = Number.isFinite(gps.satellites) ? gps.satellites : null;
  const ioElems = state.data?.io_elements?.elements || {};
  return (
    <div className={`rounded-md border ${accent} bg-card p-2.5 text-xs`} data-testid={`state-panel-${testIdSuffix}`}>
      <div className="flex items-baseline justify-between mb-1.5 flex-wrap gap-1">
        <span className={`font-semibold ${titleClr}`}>{title}</span>
        <span className="text-[10px] text-muted-foreground">{subtitle}</span>
      </div>
      {!state.found ? (
        <div className="text-amber-700 dark:text-amber-400 italic">No packet within &plusmn;10&nbsp;min.</div>
      ) : (
        <div className="space-y-0.5">
          <div className="text-[10px] text-muted-foreground">
            packet {Math.round(state.delta_sec)}s away
          </div>
          {hasFix ? (
            <div>
              <span className="text-muted-foreground">GPS:</span>{' '}
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-blue-600 hover:underline dark:text-blue-400"
                data-testid={`state-panel-${testIdSuffix}-map-link`}
              >
                {lat.toFixed(6)}, {lng.toFixed(6)} &#8599;
              </a>
            </div>
          ) : (
            <div><span className="text-muted-foreground">GPS:</span> no fix</div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            <span><span className="text-muted-foreground">Speed:</span> {speedKmh !== null ? formatSpeed(speedKmh) : '-'}</span>
            <span><span className="text-muted-foreground">Hdg:</span> {heading !== null ? `${heading.toFixed(0)}°` : '-'}</span>
            <span><span className="text-muted-foreground">Sats:</span> {sats !== null ? sats : '-'}</span>
            <span><span className="text-muted-foreground">Alt:</span> {Number.isFinite(gps.altitude) ? `${gps.altitude.toFixed(0)}m` : '-'}</span>
          </div>
          {state.data?.io_elements?.event_id != null && state.data.io_elements.event_id !== 0 && (
            <div><span className="text-muted-foreground">Event ID:</span> {state.data.io_elements.event_id}</div>
          )}
          {Object.keys(ioElems).length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                IO elements ({Object.keys(ioElems).length})
              </summary>
              <div className="mt-1 font-mono text-[10px] bg-muted/30 rounded p-1.5 max-h-28 overflow-y-auto">
                {Object.entries(ioElems).map(([k, v]) => (
                  <div key={k}><span className="text-muted-foreground">{k}:</span> {JSON.stringify(v)}</div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};



export const CommandCenter = () => {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('section');
    return ['send', 'templates', 'history', 'audit'].includes(s) ? s : 'send';
  });
  const setUrlParams = useSetUrlParams();
  const changeSection = (id) => {
    setActiveSection(id);
    // Deep-link the section; reset table deep-link params when leaving them.
    setUrlParams({ section: id === 'send' ? null : id, hist_page: null, audit_page: null });
  };
  const [loading, setLoading] = useState(false);

  // ── DnD sensors ──
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ── User date/time format ──
  const { dateFormat: userDateFmt, timeFormat: userTimeFmt, timezone: userTimezone } = useGeneralSettings();

  // ── Data states ──
  const [templates, setTemplates] = useState([]);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [recentReplies, setRecentReplies] = useState([]);

  // ── Template dialog ──
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ ...defaultTemplate });

  // ── Send state ──
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [customCommand, setCustomCommand] = useState('');
  const customCmdRef = useRef(null);
  useEffect(() => {
    const el = customCmdRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  }, [customCommand]);
  const [customProtocol, setCustomProtocol] = useState('');
  // True only when the user explicitly picks a protocol in the Custom Command
  // dropdown — auto-set protocols (from device/template selection) must NOT
  // narrow/refilter the device list.
  const [customProtocolExplicit, setCustomProtocolExplicit] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('gprs');
  const [activeSmsProvider, setActiveSmsProvider] = useState('');
  const [sendProtocolFilter, setSendProtocolFilter] = useState('');
  const [sendStatusFilter, setSendStatusFilter] = useState('');
  const [sendSearch, setSendSearch] = useState('');
  // iter561 — User filter for the Select Device table. '' = All Users, 'not_allocated' = devices with no allocated user.
  const [sendUserFilter, setSendUserFilter] = useState('');
  const [sendUserDropdownOpen, setSendUserDropdownOpen] = useState(false);
  const [sendUserSearch, setSendUserSearch] = useState('');
  // iter561 — Map of user id → { name, email } for the User column and search-by-user.
  const [userMap, setUserMap] = useState({});
  const [sendDevicesPage, setSendDevicesPage] = useState(1);
  const [sendDevicesPageSize, setSendDevicesPageSize] = usePersistedPageSize('send-devices', 10);
  const [sendResults, setSendResults] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendPreconditions, setSendPreconditions] = useState({ ...defaultSendPreconditions });

  // ── Confirmation dialog ──
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [pendingSend, setPendingSend] = useState(null);

  // ── History filters ──
  const [historyFilter, setHistoryFilter] = useState({ status: '', delivery_method: '' });
  const [historyDateRange, setHistoryDateRange] = useState({ from: undefined, to: undefined });
  const [historyDateResetKey, setHistoryDateResetKey] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useUrlPage('hist_page', { zeroIndexed: true });
  const [historyPageSize, setHistoryPageSize] = useUrlPageSize('command-history', 25, 'hist_size');
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [historySelectAllLoading, setHistorySelectAllLoading] = useState(false);
  // iter151 — client-side sort for the Command History table. The
  // backend returns the current page sorted by created_at desc; this
  // overrides the displayed order so support can surface latency
  // outliers within the page.  Three-state cycle: null → desc → asc → null.
  const [historySort, setHistorySort] = useState({ field: null, dir: 'desc' });

  // ── Audit filters ──
  const [auditSearch, setAuditSearch] = useState('');
  const [auditDateRange, setAuditDateRange] = useState({ from: undefined, to: undefined });
  const [auditDateResetKey, setAuditDateResetKey] = useState(0);
  const [auditPage, setAuditPage] = useUrlPage('audit_page', { zeroIndexed: true });
  const [auditPageSize, setAuditPageSize] = useUrlPageSize('audit-log', 25, 'audit_size');
  const [selectedAuditIds, setSelectedAuditIds] = useState([]);
  const [auditSelectAllLoading, setAuditSelectAllLoading] = useState(false);

  // ── Template filter ──
  const [templateProtocolFilter, setTemplateProtocolFilter] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [sendTemplateSearch, setSendTemplateSearch] = useState('');
  const [selectedTemplateExportIds, setSelectedTemplateExportIds] = useState([]);
  const [protocolGroupOrder, setProtocolGroupOrder] = useState([]);

  // ── Detail dialog ──
  const [detailDialog, setDetailDialog] = useState(null);

  // ── iter151 Phase 3 — Device Health card (Top-N slowest devices + YYMM rescues) ──
  const [healthDevices, setHealthDevices] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthGeneratedAt, setHealthGeneratedAt] = useState(null);

  // ── iter151 Phase 3.5 — "View Device Status at Send Time" modal ──
  // Stores: { audit_entry, states: [{imei, label, found, data, delta_sec, ...}], loading }
  const [stateAtSendDialog, setStateAtSendDialog] = useState(null);

  // ── Fetch helpers ──
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/admin/commands/templates');
      setTemplates(res.data.templates || []);
    } catch { /* ignore */ }
  }, [api]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await api.get('/admin/devices');
      setDevices(res.data.devices || res.data || []);
    } catch { /* ignore */ }
  }, [api]);

  // iter561 — Build a { userId: { name, email } } lookup so the Select Device
  // table can display and search by allocated user.
  const fetchUserMap = useCallback(async () => {
    try {
      const res = await api.get('/users/names');
      const map = {};
      (res.data || []).forEach(u => { map[u.id] = { name: u.full_name || u.email, email: u.email }; });
      setUserMap(map);
    } catch { /* ignore */ }
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/commands/stats');
      setStats(res.data);
    } catch { /* ignore */ }
  }, [api]);

  const fetchHistory = useCallback(async (page = 0) => {
    try {
      const params = new URLSearchParams({ limit: String(historyPageSize), skip: String(page * historyPageSize) });
      if (historyFilter.status) params.set('status', historyFilter.status);
      if (historyFilter.delivery_method) params.set('delivery_method', historyFilter.delivery_method);
      if (historySearch) params.set('search', historySearch);
      if (historyDateRange.from) params.set('from_date', historyDateRange.from.toISOString());
      if (historyDateRange.to) {
                params.set('to_date', historyDateRange.to.toISOString());
      }
      const res = await api.get(`/admin/commands/history?${params}`);
      setHistory(res.data.commands || []);
      setHistoryTotal(res.data.total || 0);
    } catch { /* ignore */ }
  }, [api, historyFilter, historySearch, historyDateRange, historyPageSize]);

  // iter151 — Top-N slowest devices in the last 7d, with YYMM rescue counts.
  const fetchDeviceHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await api.get('/admin/commands/device-health?days=7&limit=10');
      setHealthDevices(res.data.devices || []);
      setHealthGeneratedAt(res.data.generated_at || null);
    } catch {
      // silent — card just stays empty
    } finally {
      setHealthLoading(false);
    }
  }, [api]);

  // iter151 Phase 3.5/3.6 — fetch device state at BOTH the send time
  // and the ack time (when device replied) for every device in the
  // audit entry. Single backend call gathers everything via the new
  // /audit-context/{audit_id} endpoint so the modal renders in one
  // round-trip regardless of fleet size.
  const openStateAtSendDialog = useCallback(async (entry) => {
    if (!entry?.id) {
      setStateAtSendDialog({ entry, devices: [], loading: false, error: 'Audit entry missing id' });
      return;
    }
    setStateAtSendDialog({ entry, devices: [], loading: true });
    try {
      const res = await api.get(`/admin/commands/audit-context/${encodeURIComponent(entry.id)}?window_sec=600`);
      const devices = (res.data.devices || []).map(d => ({
        ...d,
        label: formatAuditDeviceLabel({
          asset_name: d.device?.asset_name,
          unit_number: d.device?.unit_number,
          name: d.device?.name,
        }) || d.imei || 'Unknown device',
      }));
      setStateAtSendDialog({
        entry, devices, loading: false,
        sendTime: res.data.send_time,
      });
    } catch (e) {
      setStateAtSendDialog({ entry, devices: [], loading: false, error: e?.response?.data?.detail || e?.message || 'fetch failed' });
    }
  }, [api]);


  const fetchAuditLog = useCallback(async (page = 0) => {
    try {
      const params = new URLSearchParams({ limit: String(auditPageSize), skip: String(page * auditPageSize) });
      if (auditSearch) params.set('search', auditSearch);
      if (auditDateRange.from) params.set('from_date', auditDateRange.from.toISOString());
      if (auditDateRange.to) {
                params.set('to_date', auditDateRange.to.toISOString());
      }
      const res = await api.get(`/admin/commands/audit-log?${params}`);
      setAuditLog(res.data.entries || []);
      setAuditTotal(res.data.total || 0);
    } catch { /* ignore */ }
  }, [api, auditSearch, auditDateRange, auditPageSize]);

  const fetchRecentReplies = useCallback(async () => {
    try {
      const res = await api.get('/admin/commands/recent-replies?limit=10');
      setRecentReplies(res.data.replies || []);
    } catch { /* ignore */ }
  }, [api]);

  const fetchGroupOrder = useCallback(async () => {
    try {
      const res = await api.get('/admin/commands/templates/group-order');
      setProtocolGroupOrder(res.data.order || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => {
    fetchTemplates();
    fetchDevices();
    fetchUserMap();
    fetchStats();
    fetchRecentReplies();
    fetchGroupOrder();
    api.get('/admin/commands/communications/sms').then(res => {
      setActiveSmsProvider(res.data?.primary_provider || 'twilio');
    }).catch(() => {});
  }, [fetchTemplates, fetchDevices, fetchUserMap, fetchStats, fetchRecentReplies, fetchGroupOrder, api]);

  useEffect(() => {
    if (activeSection === 'history') {
      fetchHistory(historyPage);
      fetchDeviceHealth();
    }
  }, [activeSection, fetchHistory, fetchDeviceHealth, historyPage]);

  // iter151 — refresh the Device Health card every 60s while History is open.
  useEffect(() => {
    if (activeSection !== 'history') return undefined;
    const id = setInterval(fetchDeviceHealth, 60000);
    return () => clearInterval(id);
  }, [activeSection, fetchDeviceHealth]);

  // iter892 — Sequence Live Status: while any visible command is still
  // executing (queued/sending), poll the history every 2s so per-step
  // sequence progress updates live.
  useEffect(() => {
    if (activeSection !== 'history') return undefined;
    const hasLive = history.some(c => c.status === 'queued' || c.status === 'sending');
    if (!hasLive) return undefined;
    const id = setInterval(() => fetchHistory(historyPage), 2000);
    return () => clearInterval(id);
  }, [activeSection, history, fetchHistory, historyPage]);

  // Keep the open detail dialog in sync with live-refreshed history rows.
  useEffect(() => {
    if (!detailDialog?.id) return;
    const updated = history.find(c => c.id === detailDialog.id);
    if (updated && updated !== detailDialog) setDetailDialog(updated);
  }, [history, detailDialog]);

  useEffect(() => {
    if (activeSection === 'audit') fetchAuditLog(auditPage);
  }, [activeSection, fetchAuditLog, auditPage]);

  // Reset audit page to 0 whenever filters change so the user always
  // sees the first matches when they narrow the query.
  useSkipFirstEffect(() => {
    setAuditPage(0);
  }, [auditSearch, auditDateRange, auditPageSize]);

  // Same for command history filters.
  useSkipFirstEffect(() => {
    setHistoryPage(0);
  }, [historyFilter, historySearch, historyDateRange, historyPageSize]);

  // Reset the Send-Devices pagination whenever filters change so the user
  // always sees the first matches.
  useEffect(() => {
    setSendDevicesPage(1);
  }, [sendSearch, sendProtocolFilter, sendStatusFilter, sendUserFilter, sendDevicesPageSize]);

  // ── Template CRUD ──
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ ...defaultTemplate, preconditions: { ...defaultTemplate.preconditions } });
    setTemplateDialog(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      ...t,
      preconditions: { ...defaultTemplate.preconditions, ...(t.preconditions || {}) },
      command_sequence: t.command_sequence || [],
    });
    setTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name) { toast.error('Name is required'); return; }
    if (!templateForm.protocol) { toast.error('Protocol is required'); return; }
    if (templateForm.command_mode === 'single' && !templateForm.command_text) { toast.error('Command text is required'); return; }
    if (templateForm.command_mode === 'sequence' && (!templateForm.command_sequence || templateForm.command_sequence.length < 2)) {
      toast.error('Sequence requires at least 2 steps'); return;
    }
    setLoading(true);
    try {
      if (editingTemplate) {
        await api.put(`/admin/commands/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/admin/commands/templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialog(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally { setLoading(false); }
  };

  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState(null);

  const deleteTemplate = (template) => setDeleteTemplateTarget(template);

  const doDeleteTemplate = async () => {
    if (!deleteTemplateTarget) return;
    try {
      await api.delete(`/admin/commands/templates/${deleteTemplateTarget.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleTemplateDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Find which protocol group this belongs to
    const activeTemplate = templates.find(t => t.id === active.id);
    const overTemplate = templates.find(t => t.id === over.id);
    if (!activeTemplate || !overTemplate || activeTemplate.protocol !== overTemplate.protocol) return;
    const proto = activeTemplate.protocol;
    const protoTemplates = templates.filter(t => t.protocol === proto);
    const oldIndex = protoTemplates.findIndex(t => t.id === active.id);
    const newIndex = protoTemplates.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(protoTemplates, oldIndex, newIndex);
    // Rebuild full list preserving order of other protocols
    const newTemplates = templates.filter(t => t.protocol !== proto);
    // Insert reordered group at the position of the first item of that protocol
    const insertIdx = templates.findIndex(t => t.protocol === proto);
    newTemplates.splice(insertIdx >= 0 ? insertIdx : newTemplates.length, 0, ...reordered);
    setTemplates(newTemplates);
    // Persist full order
    try {
      await api.put('/admin/commands/templates/reorder', { order: newTemplates.map(t => t.id) });
    } catch { toast.error('Failed to save order'); }
  };

  const handleGroupDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = protocolGroupOrder.indexOf(active.id);
    const newIndex = protocolGroupOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(protocolGroupOrder, oldIndex, newIndex);
    setProtocolGroupOrder(newOrder);
    try {
      await api.put('/admin/commands/templates/group-order', { order: newOrder });
    } catch { toast.error('Failed to save group order'); }
  };

  // ── Device list protocol filter ──
  const activeProtocol = customProtocol;

  // ── Filtered devices for send section (sorted alphabetically) ──
  const filteredDevices = devices.filter(d => {
    if (sendProtocolFilter && d.protocol !== sendProtocolFilter) return false;
    // Custom protocol narrows the device list ONLY when explicitly picked by
    // the user (auto-set from device selection must not refilter the list).
    if (customProtocolExplicit && activeProtocol && d.protocol !== activeProtocol) return false;
    if (sendStatusFilter) {
      if (sendStatusFilter === 'online') {
        if (!['moving', 'idle', 'stopped'].includes(d.status)) return false;
      } else {
        if (d.status !== sendStatusFilter) return false;
      }
    }
    // iter561 — User filter: '' = All Users, 'not_allocated' = no allocated user,
    // otherwise device must have this user id in allocated_tenant_ids.
    if (sendUserFilter) {
      const allocated = d.allocated_tenant_ids || [];
      if (sendUserFilter === 'not_allocated') {
        if (allocated.length > 0) return false;
      } else if (!allocated.includes(sendUserFilter)) {
        return false;
      }
    }
    if (sendSearch) {
      const s = sendSearch.toLowerCase();
      // iter561 — include allocated user name / email in the searchable text.
      const userText = (d.allocated_tenant_ids || [])
        .map(uid => {
          const u = userMap[uid];
          if (!u) return '';
          return `${u.name || ''} ${u.email || ''}`.toLowerCase();
        })
        .join(' ');
      if (!(
        (d.name || '').toLowerCase().includes(s) ||
        (d.asset_name || '').toLowerCase().includes(s) ||
        (d.unit_number || '').toLowerCase().includes(s) ||
        (d.imei || '').toLowerCase().includes(s) ||
        userText.includes(s)
      )) return false;
    }
    if (['Digital Matter', 'BLE Beacon'].includes(d.protocol)) return false;
    if (d.device_type === 'beacon') return false;
    return true;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // ── Same-protocol selection helpers ──
  // Selection is restricted to a single protocol. Derive it from the first
  // selected device; the visible page drives the header Select-All checkbox.
  const visiblePageDevices = paginateArray(filteredDevices, sendDevicesPage, sendDevicesPageSize);
  const visiblePageProtocols = [...new Set(visiblePageDevices.map(d => d.protocol))];
  const selectionProtocol = selectedDeviceIds.length > 0
    ? (devices.find(d => d.id === selectedDeviceIds[0])?.protocol || null)
    : null;
  const selectAllDisabled = visiblePageProtocols.length > 1
    || (!!selectionProtocol && visiblePageProtocols.length === 1 && visiblePageProtocols[0] !== selectionProtocol);
  const sameProtocolDevices = selectionProtocol
    ? filteredDevices.filter(d => d.protocol === selectionProtocol)
    : filteredDevices;

  // ── User filter (searchable dropdown) ──
  const allocatedUserOptions = Object.entries(userMap)
    .filter(([uid]) => devices.some(d => (d.allocated_tenant_ids || []).includes(uid)))
    .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''))
    .map(([uid, u]) => ({ value: uid, label: u.name || u.email, email: u.email || '' }));
  const sendUserFilterLabel = !sendUserFilter
    ? 'All Users'
    : sendUserFilter === 'not_allocated'
      ? 'Not Allocated'
      : (userMap[sendUserFilter]?.name || userMap[sendUserFilter]?.email || 'User');

  // ── Send handler ──
  const handleSend = async (isDryRun = false) => {
    if (selectedDeviceIds.length === 0) { toast.error('Select at least one device'); return; }
    if (!selectedTemplateId && !customCommand) { toast.error('Select a template or enter a custom command'); return; }

    // Check risk level for confirmation
    if (!isDryRun && selectedTemplateId) {
      const tmpl = templates.find(t => t.id === selectedTemplateId);
      if (tmpl && ['high', 'critical'].includes(tmpl.risk_level)) {
        const expectedText = `SEND ${selectedDeviceIds.length} COMMAND${selectedDeviceIds.length > 1 ? 'S' : ''}`;
        setPendingSend({ isDryRun });
        setConfirmText('');
        setConfirmDialog({ expectedText, riskLevel: tmpl.risk_level, templateName: tmpl.name });
        return;
      }
    }

    await executeSend(isDryRun);
  };

  const executeSend = async (isDryRun = false) => {
    setSending(true);
    setSendResults(null);
    try {
      const res = await api.post('/admin/commands/send', {
        device_ids: selectedDeviceIds,
        template_id: selectedTemplateId || undefined,
        custom_command: customCommand || undefined,
        delivery_method: deliveryMethod,
        dry_run: isDryRun,
        preconditions: (sendPreconditions.require_online || sendPreconditions.require_speed_zero || sendPreconditions.require_ignition_off || sendPreconditions.require_ignition_on || sendPreconditions.max_last_seen_minutes) ? sendPreconditions : undefined,
      });
      setSendResults(res.data);
      if (!isDryRun) {
        const { sent, skipped, failed, results } = res.data;
        if (sent > 0 && failed === 0) {
          toast.success(`${sent} command(s) sent${skipped ? `, ${skipped} skipped` : ''}`);
        } else if (failed > 0 && sent === 0) {
          const reason = results?.find(r => r.status === 'failed')?.error || 'Check device SIM/mobile number';
          toast.error(`All ${failed} command(s) failed: ${reason}`);
        } else if (failed > 0) {
          toast.warning(`${sent} sent, ${failed} failed, ${skipped} skipped`);
        } else {
          toast.info(`${sent} sent, ${skipped} skipped`);
        }
        setCustomCommand('');
        setCustomProtocol('');
        fetchStats();
        fetchRecentReplies();
        // Auto-check replies after 5 seconds, then again at 15 seconds for slower devices
        const checkReplies = async (showToast) => {
          try {
            if (deliveryMethod !== 'gprs') {
              const r = await api.post('/admin/commands/check-sms-replies');
              if (showToast && (r.data.new > 0 || r.data.linked > 0)) {
                toast.success(`${r.data.new} new reply(s), ${r.data.linked} linked`);
              }
            }
            fetchRecentReplies();
            if (activeSection === 'history') fetchHistory(historyPage);
          } catch { /* ignore */ }
        };
        setTimeout(() => checkReplies(true), 5000);
        setTimeout(() => checkReplies(true), 15000);
        // Devices can take 30–120s to reply by SMS — keep checking a little longer
        setTimeout(() => checkReplies(false), 45000);
        setTimeout(() => checkReplies(false), 90000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const confirmAndSend = () => {
    if (!confirmDialog) return;
    if (confirmText !== confirmDialog.expectedText) {
      toast.error(`Please type "${confirmDialog.expectedText}" exactly`);
      return;
    }
    setConfirmDialog(false);
    executeSend(false);
  };

  const resetSendConfiguration = () => {
    setSelectedTemplateId(null);
    setCustomCommand('');
    setCustomProtocol('');
    setCustomProtocolExplicit(false);
    setDeliveryMethod('gprs');
    setSendPreconditions({ ...defaultSendPreconditions });
    setSendResults(null);
  };

  const updateSelectedDevices = (nextDeviceIds) => {
    const hadDeselection = selectedDeviceIds.some(id => !nextDeviceIds.includes(id));
    setSelectedDeviceIds(nextDeviceIds);
    if (hadDeselection) {
      resetSendConfiguration();
    }
    // Auto-set Custom Command protocol to the last selected device's protocol
    // (auto-set only — never narrows the device list).
    if (nextDeviceIds.length > 0) {
      const lastId = nextDeviceIds[nextDeviceIds.length - 1];
      const lastDevice = devices.find(d => d.id === lastId);
      if (lastDevice?.protocol) {
        setCustomProtocol(lastDevice.protocol);
        setCustomProtocolExplicit(false);
      }
    }
  };

  // ── Select all / none ──
  // Header checkbox scopes to the currently VISIBLE page and is DISABLED when
  // the page shows mixed protocols (selection must stay single-protocol).
  // The selection-bar "Select All" selects every filtered device that matches
  // the selection's protocol.
  const toggleAllDevices = () => {
    if (selectAllDisabled) return;
    const visibleIds = visiblePageDevices.map(d => d.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedDeviceIds.includes(id));
    if (allVisibleSelected) {
      updateSelectedDevices(selectedDeviceIds.filter(id => !visibleIds.includes(id)));
    } else {
      updateSelectedDevices(Array.from(new Set([...selectedDeviceIds, ...visibleIds])));
    }
  };

  const selectAllFilteredDevices = () => {
    if (selectedDeviceIds.length >= sameProtocolDevices.length && sameProtocolDevices.length > 0) {
      updateSelectedDevices([]);
    } else {
      updateSelectedDevices(sameProtocolDevices.map(d => d.id));
    }
  };

  // ── Section nav items ──
  const sections = [
    { id: 'send', label: 'Send Commands', icon: Send },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'history', label: 'History', icon: History },
    { id: 'audit', label: 'Audit Log', icon: Shield },
  ];

  // ── Export/Import helpers ──
  // Filename timestamp in the user's configured timezone (Settings > Date & Time), not UTC/browser.
  const getExportTimestamp = () => getFileTimestamp();

  const downloadFile = (content, filename, type = 'application/json') => {
    const prefix = type.includes('csv') ? '\uFEFF' : '';
    const blob = new Blob([prefix + content], { type: type.includes('csv') ? 'text/csv;charset=utf-8;' : type });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleExportTemplates = async () => {
    try {
      const params = templateProtocolFilter ? `?protocol=${templateProtocolFilter}` : '';
      const res = await api.get(`/admin/commands/templates/export${params}`);
      let toExport = res.data.templates || [];
      if (selectedTemplateExportIds.length > 0) {
        toExport = toExport.filter(t => selectedTemplateExportIds.includes(t.id));
      }
      if (toExport.length === 0) { toast.info('No templates to export'); return; }
      const ts = getExportTimestamp();
      downloadFile(JSON.stringify({ templates: toExport, total: toExport.length, exported_at: new Date().toISOString() }, null, 2), `command_templates_${ts}.json`);
      toast.success(`Exported ${toExport.length} template(s)`);
    } catch { toast.error('Failed to export templates'); }
  };

  const handleImportTemplates = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const items = data.templates || data;
        if (!Array.isArray(items) || items.length === 0) { toast.error('No templates found in file'); return; }
        const res = await api.post('/admin/commands/templates/import', { templates: items, mode: 'merge' });
        toast.success(`Imported ${res.data.imported} template(s)${res.data.skipped ? `, ${res.data.skipped} skipped (duplicates)` : ''}`);
        fetchTemplates();
        fetchStats();
      } catch { toast.error('Failed to parse or import templates'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportHistory = async () => {
    try {
      let records;
      if (selectedHistoryIds.length > 0) {
        records = history.filter(r => selectedHistoryIds.includes(r.id));
        // Selection spans other pages — fetch the full filtered set once and
        // pick out the remainder (mirrors Driver Corrections export).
        if (records.length < selectedHistoryIds.length) {
          const params = new URLSearchParams();
          if (historyFilter.status) params.set('status', historyFilter.status);
          if (historyFilter.delivery_method) params.set('delivery_method', historyFilter.delivery_method);
          if (historySearch) params.set('search', historySearch);
          if (historyDateRange.from) params.set('from_date', historyDateRange.from.toISOString());
          if (historyDateRange.to) {
                        params.set('to_date', historyDateRange.to.toISOString());
          }
          const res = await api.get(`/admin/commands/history/export?${params}`);
          records = (res.data.records || []).filter(r => selectedHistoryIds.includes(r.id));
        }
      } else {
        const params = new URLSearchParams();
        if (historyFilter.status) params.set('status', historyFilter.status);
        if (historyFilter.delivery_method) params.set('delivery_method', historyFilter.delivery_method);
        if (historySearch) params.set('search', historySearch);
        if (historyDateRange.from) params.set('from_date', historyDateRange.from.toISOString());
        if (historyDateRange.to) {
                    params.set('to_date', historyDateRange.to.toISOString());
        }
        const res = await api.get(`/admin/commands/history/export?${params}`);
        records = res.data.records || [];
      }
      if (records.length === 0) { toast.info('No records to export'); return; }
      const headers = ['Time', 'Device Name', 'IMEI', 'ICCID', 'Mobile Number', 'Command', 'Method', 'Status', 'Replied', 'Latency (s)', 'Response'];
      const rows = records.map(r => {
        // iter151 — surface device-reply timing alongside the original command
        // so support can tell "device was slow" vs "our code was slow" at a glance.
        const start = r.sent_at || r.created_at;
        let latency = '';
        if (r.responded_at && start) {
          const seconds = (new Date(r.responded_at).getTime() - new Date(start).getTime()) / 1000;
          if (Number.isFinite(seconds) && seconds >= 0) latency = seconds.toFixed(2);
        }
        return [
          r.created_at ? formatDate(r.created_at) : '', formatCommandDeviceLabel(r), r.device_imei || '', r.iccid || '', r.mobile_number || '',
          r.command || r.message || '', r.delivery_method || 'gprs', r.status || '',
          r.responded_at ? formatDate(r.responded_at) : '', latency,
          r.response || r.error || '',
        ];
      });
      const imeiCols = [2, 3, 4]; // IMEI, ICCID and Mobile Number columns
      const csv = [headers.join(','), ...rows.map(r => r.map((v, ci) => {
        const s = String(v).replace(/"/g, '""');
        return imeiCols.includes(ci) && s ? `="${s}"` : `"${s}"`;
      }).join(','))].join('\n');
      const ts = getExportTimestamp();
      downloadFile(csv, `command_history_${ts}.csv`, 'text/csv');
      toast.success(`Exported ${records.length} record(s)`);
    } catch { toast.error('Failed to export history'); }
  };

  const handleExportAuditLog = async () => {
    try {
      let entries;
      if (selectedAuditIds.length > 0) {
        entries = auditLog.filter(e => selectedAuditIds.includes(e.id));
        if (entries.length < selectedAuditIds.length) {
          const params = new URLSearchParams();
          if (auditSearch) params.set('search', auditSearch);
          if (auditDateRange.from) params.set('from_date', auditDateRange.from.toISOString());
          if (auditDateRange.to) {
                        params.set('to_date', auditDateRange.to.toISOString());
          }
          const res = await api.get(`/admin/commands/audit-log/export?${params}`);
          entries = (res.data.entries || []).filter(e => selectedAuditIds.includes(e.id));
        }
      } else {
        const params = new URLSearchParams();
        if (auditSearch) params.set('search', auditSearch);
        if (auditDateRange.from) params.set('from_date', auditDateRange.from.toISOString());
        if (auditDateRange.to) {
                    params.set('to_date', auditDateRange.to.toISOString());
        }
        const res = await api.get(`/admin/commands/audit-log/export?${params}`);
        entries = res.data.entries || [];
      }
      if (entries.length === 0) { toast.info('No entries to export'); return; }
      const headers = ['Time', 'Action', 'User', 'Role', 'IP Address', 'Device', 'ICCID', 'Template Name', 'Protocol', 'Risk Level', 'Command', 'Delivery Method', 'Device Count', 'Sent', 'Skipped', 'Failed', 'Provider'];
      const rows = entries.map(e => {
        const d = e.details || {};
        const after = d.after || {};
        const dr = d.device_results || [];
        const deviceNames = (d.device_names || dr.map(r => r.device_name || r.imei)).filter(Boolean).join('; ');
        const iccids = dr.map(r => r.iccid).filter(Boolean).join('; ');
        return [
          formatUserDateTime(e.created_at, userDateFmt, userTimeFmt, userTimezone, { showSeconds: true }), (e.action || '').replace(/_/g, ' '), e.user_email || '', e.user_role || '',
          e.ip_address || '', deviceNames || '', iccids || '',
          d.name || after.name || '', after.protocol || d.protocol || '', after.risk_level || d.risk_level || '',
          d.custom_command || after.command_text || '', d.delivery_method || after.preferred_delivery_method || '',
          d.device_count || '', d.sent !== undefined ? d.sent : '', d.skipped || '', d.failed || '',
          d.provider || '',
        ];
      });
      const auditNumericCols = [5, 6]; // Device IMEI and ICCID column indices
      const csv = [headers.join(','), ...rows.map(r => r.map((v, ci) => {
        const s = String(v).replace(/"/g, '""');
        return auditNumericCols.includes(ci) && s ? `="${s}"` : `"${s}"`;
      }).join(','))].join('\n');
      const ts = getExportTimestamp();
      downloadFile(csv, `command_audit_log_${ts}.csv`, 'text/csv');
      toast.success(`Exported ${entries.length} entry(ies)`);
    } catch { toast.error('Failed to export audit log'); }
  };

  const formatDate = (d) => {
    return formatUserDateTime(d, userDateFmt, userTimeFmt, userTimezone, { showSeconds: true });
  };

  // iter151 follow-up — Project standing rule: device labels across the
  // Commands page must read "<Asset Name> / <Unit Number>" (only the
  // parts that exist; fall back to `name`, then `imei`).  The helper
  // lives in /utils/deviceLabel.js so the rule is enforced consistently
  // across the whole app — see phase plan in PRD.md.
  // (Inline copies were removed in iter151 Phase 1; keep using imports.)

  // Build an IMEI → device lookup so the History table (which only has
  // a snapshot `device_name` on each command_doc) can resolve to the
  // device's CURRENT asset_name + unit_number.
  const devicesByImei = useMemo(() => buildDevicesByImei(devices), [devices]);

  // Format a command row's device for display.  Bound to the local
  // devicesByImei so call-sites stay terse.
  const formatCommandDeviceLabel = useCallback(
    (cmd) => _formatCommandDeviceLabel(cmd, devicesByImei),
    [devicesByImei],
  );

  // iter151 — device-reply latency in seconds (sent_at → responded_at,
  // falling back to created_at). Returns a short human label like "1.18s"
  // or "42.51s" — or '-' if either timestamp is missing.
  const formatReplyLatency = (cmd) => {
    if (!cmd?.responded_at) return '-';
    const start = cmd.sent_at || cmd.created_at;
    if (!start) return '-';
    const t1 = new Date(start).getTime();
    const t2 = new Date(cmd.responded_at).getTime();
    if (!Number.isFinite(t1) || !Number.isFinite(t2) || t2 < t1) return '-';
    const seconds = (t2 - t1) / 1000;
    if (seconds < 10) return `${seconds.toFixed(2)}s`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const rem = Math.round(seconds % 60);
    return `${mins}m ${rem}s`;
  };

  // iter151 — Sort key extractors for the Command History columns. Each
  // returns a primitive (number or epoch-ms timestamp) so Array.sort can
  // compare without surprises. Rows whose key is null/undefined sink to
  // the BOTTOM regardless of sort direction so users browsing for
  // outliers don't have to skim past "-" rows.
  const historySortKey = (cmd, field) => {
    if (field === 'latency') {
      if (!cmd?.responded_at) return null;
      const start = cmd.sent_at || cmd.created_at;
      if (!start) return null;
      const t1 = new Date(start).getTime();
      const t2 = new Date(cmd.responded_at).getTime();
      if (!Number.isFinite(t1) || !Number.isFinite(t2) || t2 < t1) return null;
      return t2 - t1;
    }
    if (field === 'created_at') {
      const t = new Date(cmd?.created_at || 0).getTime();
      return Number.isFinite(t) ? t : null;
    }
    return null;
  };

  const cycleHistorySort = (field) => {
    setHistorySort(prev => {
      if (prev.field !== field) return { field, dir: 'desc' };
      if (prev.dir === 'desc') return { field, dir: 'asc' };
      return { field: null, dir: 'desc' };  // 3rd click clears
    });
  };

  const sortedHistory = useMemo(() => {
    if (!historySort.field) return history;
    const sign = historySort.dir === 'asc' ? 1 : -1;
    return [...history].sort((a, b) => {
      const va = historySortKey(a, historySort.field);
      const vb = historySortKey(b, historySort.field);
      // Null/undefined keys always sink to the bottom.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return (va - vb) * sign;
    });
  }, [history, historySort]);

  const getDeviceStatusBadge = (status, deviceId) => {
    const statusConfig = {
      online: { color: 'bg-green-600', label: 'Online', icon: Wifi },
      offline: { color: 'bg-gray-500', label: 'Offline', icon: WifiOff },
      moving: { color: 'bg-blue-600', label: 'Moving', icon: Activity },
      stopped: { color: 'bg-red-600', label: 'Stopped', icon: Circle },
      idle: { color: 'bg-yellow-600', label: 'Idle', icon: Circle },
    };

    const config = statusConfig[status] || statusConfig.offline;
    const Icon = config.icon;

    return (
      <Badge
        className={`${config.color} text-white`}
        data-testid={`select-device-status-badge-${deviceId || status || 'unknown'}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Linkify URLs in text
  const linkifyText = (text) => {
    if (!text) return '-';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return text;
    return parts.map((part, i) =>
      urlRegex.test(part)
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" onClick={e => e.stopPropagation()}>{part}</a>
        : part
    );
  };

  return (
    <div className="space-y-6" data-testid="command-center-page">
      {/* Page header — matches User/Device/Beacon Management styling. */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <Terminal className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="commands-title">Commands</h1>
          <p className="text-sm text-muted-foreground">Send remote commands, review history, and manage command templates</p>
        </div>
      </div>

      {/* Section Nav */}
      <MobileSectionMenu
        items={sections}
        activeId={activeSection}
        onChange={changeSection}
        testIdPrefix="command-section"
      />
      <div className="hidden sm:flex gap-2 flex-wrap" data-testid="command-section-nav">
        {sections.map(s => (
          <Button size="sm" key={s.id}
            onClick={() => changeSection(s.id)}
            className={`gap-2 min-w-[180px] justify-center ${activeSection === s.id
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid={`section-${s.id}`}
          >
            <s.icon className="w-4 h-4" />
            {s.label}
          </Button>
        ))}
      </div>

      {/* Stats badges — moved below the tabs and rendered as a badge row
          matching Shared Evidence / Impersonation Audit layouts. */}
      {stats && (
        <div className="flex flex-wrap items-center gap-2" data-testid="command-stats">
          <Badge variant="outline" className="gap-1" data-testid="command-stat-today">
            <BarChart3 className="w-3 h-3" /> {stats.today} Today
          </Badge>
          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" data-testid="command-stat-week">
            <Zap className="w-3 h-3" /> {stats.this_week} This Week
          </Badge>
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 gap-1" data-testid="command-stat-success">
            <CheckCircle className="w-3 h-3" /> {stats.success_rate}% Success
          </Badge>
          <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 gap-1" data-testid="command-stat-templates">
            <FileText className="w-3 h-3" /> {stats.template_count} Templates
          </Badge>
        </div>
      )}

      {/* ═══════════════════════ SEND COMMANDS SECTION ═══════════════════════ */}
      {activeSection === 'send' && (
        <div className="space-y-3" data-testid="send-section">
          {/* ── Top Bar: Custom Command + Delivery + Preconditions | Action Buttons ── */}
          <div className="flex flex-wrap items-stretch gap-3">
            {/* Custom Command - inline */}
            <Card className="flex-1 min-w-[280px]">
              <CardContent className="px-3 py-2 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground">Custom Command</p>
                <Select value={customProtocol || ''} onValueChange={v => { const proto = v === 'all_custom_protocols' ? '' : v; setCustomProtocol(proto); setCustomProtocolExplicit(!!proto); setSelectedDeviceIds([]); }}>
                  <SelectTrigger className="h-7 text-xs bg-slate-100 dark:bg-[#0D1425] border-slate-300 dark:border-slate-700" data-testid="custom-protocol-select">
                    <SelectValue placeholder="Protocol (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_custom_protocols">All Protocols</SelectItem>
                    {[...PROTOCOLS].sort().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea
                  ref={customCmdRef}
                  placeholder="Enter raw command text..."
                  value={customCommand}
                  onChange={e => { setCustomCommand(e.target.value); if (e.target.value) setSelectedTemplateId(null); }}
                  className="font-mono text-xs resize-none overflow-hidden bg-slate-100 dark:bg-[#0D1425] border-slate-300 dark:border-slate-700"
                  rows={1}
                  data-testid="custom-command-input"
                />
                <p className="text-[9px] text-muted-foreground leading-tight">PIN/password & protocol formatting applied automatically via SMS.</p>
              </CardContent>
            </Card>

            {/* Delivery Method - compact inline */}
            <Card className="min-w-[160px]">
              <CardContent className="px-3 py-2 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground">Delivery Method</p>
                {DELIVERY_METHODS.map(dm => (
                  <div
                    key={dm.value}
                    className={`flex items-center gap-2 px-2 py-0.5 rounded cursor-pointer border transition-colors ${
                      deliveryMethod === dm.value ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-transparent hover:bg-muted/50'
                    }`}
                    onClick={() => setDeliveryMethod(dm.value)}
                    data-testid={`delivery-${dm.value}`}
                  >
                    <dm.icon className={`w-3.5 h-3.5 ${deliveryMethod === dm.value ? 'text-blue-600' : 'text-muted-foreground'}`} />
                    <span className="text-xs">{dm.label || (dm.value === 'sms_3rdparty' ? `SMS (3rd Party - ${activeSmsProvider === 'mobilemessage' ? 'MM' : 'Twilio'})` : dm.value)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Preconditions - compact inline */}
            <Card className="min-w-[200px]">
              <CardContent className="px-3 py-2 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" /> Preconditions</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <Switch className="scale-75" checked={sendPreconditions.require_online} onCheckedChange={v => setSendPreconditions(p => ({ ...p, require_online: v }))} />
                    <span className="text-[11px]">Online</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch className="scale-75" checked={sendPreconditions.require_speed_zero} onCheckedChange={v => setSendPreconditions(p => ({ ...p, require_speed_zero: v }))} />
                    <span className="text-[11px]">Speed=0</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch className="scale-75" checked={sendPreconditions.require_ignition_off} onCheckedChange={v => setSendPreconditions(p => ({ ...p, require_ignition_off: v, require_ignition_on: v ? false : p.require_ignition_on }))} />
                    <span className="text-[11px]">Ign OFF</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch className="scale-75" checked={sendPreconditions.require_ignition_on} onCheckedChange={v => setSendPreconditions(p => ({ ...p, require_ignition_on: v, require_ignition_off: v ? false : p.require_ignition_off }))} />
                    <span className="text-[11px]">Ign ON</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] whitespace-nowrap">Max Last Seen:</span>
                  <Input type="number" className="h-6 w-16 text-xs dark:bg-[#172031]"
                    value={sendPreconditions.max_last_seen_minutes || ''}
                    onChange={e => setSendPreconditions(p => ({ ...p, max_last_seen_minutes: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="min" />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card className="min-w-[160px] flex items-center">
              <CardContent className="px-3 py-2 flex flex-col gap-2 justify-center w-full">
                <Button
                  onClick={() => handleSend(true)}
                  variant="outline"
                  className="gap-2 w-full"
                  disabled={sending || (selectedDeviceIds.length === 0)}
                  data-testid="dry-run-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Dry Run Preview
                </Button>
                <Button
                  onClick={() => handleSend(false)}
                  className="gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white w-full"
                  disabled={sending || (selectedDeviceIds.length === 0)}
                  data-testid="send-commands-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Command{selectedDeviceIds.length > 1 ? 's' : ''}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* ── Main Area: Command Templates (left) | Device Selector + tables (right) ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[290px_1fr] gap-3">
            {/* Left: Command Templates */}
            <div>
              <Card className="h-full">
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm">Command Templates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 pt-0 px-4 pb-3">
                  {selectedDeviceIds.length === 0 ? (
                    <div
                      className="rounded-lg border border-dashed border-slate-300 bg-slate-50/70 px-3 py-4 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300"
                      data-testid="template-selection-helper"
                    >
                      <p className="font-medium text-slate-700 dark:text-slate-200">Templates appear when a device is selected.</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">Select one or more devices first.</p>
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No templates yet. Create one in the Templates section.</p>
                  ) : (() => {
                    const selectedProtocols = new Set(
                      selectedDeviceIds.map(id => devices.find(d => d.id === id)?.protocol).filter(Boolean)
                    );
                    const searchLower = sendTemplateSearch.toLowerCase();
                    const compatibleTemplates = [...templates]
                      .filter(t => selectedProtocols.has(t.protocol))
                      .filter(t => !searchLower || (t.name || '').toLowerCase().includes(searchLower) || (t.description || '').toLowerCase().includes(searchLower) || (t.command_text || '').toLowerCase().includes(searchLower));

                    return (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          placeholder="Search templates..."
                          value={sendTemplateSearch}
                          onChange={e => setSendTemplateSearch(e.target.value)}
                          className="h-7 text-xs pl-8 pr-8"
                          data-testid="send-template-search"
                        />
                        {sendTemplateSearch && <button onClick={() => setSendTemplateSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
                      </div>
                      {compatibleTemplates.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No templates match{sendTemplateSearch ? ' your search' : ' the selected device protocol(s)'}.</p>
                      ) : (
                      <div className="flex flex-col gap-1.5">
                      {compatibleTemplates.map(t => {
                        const isSelected = selectedTemplateId === t.id;
                        return (
                        <Button
                          key={t.id}
                          size="sm"
                          className={`text-xs justify-center whitespace-nowrap ${isSelected
                              ? 'ring-2 ring-offset-2 ring-slate-400 dark:ring-slate-500 shadow-lg ' + (BTN_COLOR_MAP[t.button_color] || BTN_COLOR_MAP.blue)
                              : (BTN_COLOR_MAP[t.button_color] || BTN_COLOR_MAP.blue)
                          }`}
                          onClick={() => {
                            if (selectedTemplateId === t.id) {
                              setSelectedTemplateId(null);
                              setCustomCommand('');
                              // Restore protocol from selected device (don't clear filter)
                              const selDev = selectedDeviceIds.length > 0 ? devices.find(d => d.id === selectedDeviceIds[selectedDeviceIds.length - 1]) : null;
                              setCustomProtocol(selDev?.protocol || '');
                              setCustomProtocolExplicit(false);
                              setSendPreconditions({ ...defaultSendPreconditions });
                            } else {
                              setSelectedTemplateId(t.id);
                              setCustomCommand(t.command_mode === 'sequence'
                                ? (t.command_sequence || []).map(s => s.command_text).join(' → ')
                                : (t.command_text || ''));
                              setCustomProtocol(t.protocol || '');
                              setCustomProtocolExplicit(false);
                              if (t.preferred_delivery_method) setDeliveryMethod(t.preferred_delivery_method);
                              if (t.preconditions) {
                                setSendPreconditions({
                                  require_online: t.preconditions.require_online || false,
                                  require_speed_zero: t.preconditions.require_speed_zero || false,
                                  require_ignition_off: t.preconditions.require_ignition_off || false,
                                  require_ignition_on: t.preconditions.require_ignition_on || false,
                                  max_last_seen_minutes: t.preconditions.max_last_seen_minutes || null,
                                });
                              }
                            }
                          }}
                          data-testid={`template-btn-${t.id}`}
                        >
                          {t.name}
                          {t.risk_level !== 'low' && (
                            <AlertTriangle className="w-3 h-3 ml-1" />
                          )}
                        </Button>
                        );
                      })}
                    </div>
                    )}
                    </div>
                    );
                  })()}
                  {selectedDeviceIds.length > 0 && selectedTemplateId && (() => {
                    const t = templates.find(x => x.id === selectedTemplateId);
                    return t ? (
                      <div className="mt-2 p-2 rounded-md bg-muted/50 text-xs space-y-1 overflow-hidden">
                        <p className="break-words"><span className="font-medium">Protocol:</span> {t.protocol}</p>
                        <p className="break-words"><span className="font-medium">Command:</span> <code className="bg-muted px-1 rounded break-all">{t.command_mode === 'sequence' ? `${(t.command_sequence || []).length} steps` : t.command_text}</code></p>
                        {t.description && <p className="text-muted-foreground break-words">{t.description}</p>}
                        <div className="flex gap-1 flex-wrap">
                          <Badge className={`text-[10px] ${RISK_COLORS[t.risk_level]}`}>{t.risk_level}</Badge>
                          {t.preconditions?.require_online && <Badge variant="outline" className="text-[10px]">Online</Badge>}
                          {t.preconditions?.require_speed_zero && <Badge variant="outline" className="text-[10px]">Speed=0</Badge>}
                          {t.preconditions?.require_ignition_off && <Badge variant="outline" className="text-[10px]">Ign OFF</Badge>}
                          {t.preconditions?.require_ignition_on && <Badge variant="outline" className="text-[10px]">Ign ON</Badge>}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Right: Device Selector + Results */}
            <div className="space-y-3">
              {/* Dry-run Preview — shown above device table */}
              {sendResults && sendResults.dry_run && (
                <Card data-testid="send-results">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Eye className="w-3.5 h-3.5 text-blue-500" />
                      Dry Run Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    <div className="flex gap-4 mb-2 text-xs">
                      <span className="text-green-600 font-medium">{sendResults.sent} will send</span>
                      <span className="text-yellow-600 font-medium">{sendResults.skipped} skipped</span>
                      <span className="text-red-600 font-medium">{sendResults.failed} failed</span>
                    </div>
                    <div className="max-h-[160px] overflow-y-auto border rounded-md">
                      <Table className="text-xs">
                        <TableHeader>
                          <TableRow className="h-8">
                            <TableHead className="py-1">Device</TableHead>
                            <TableHead className="py-1">IMEI</TableHead>
                            <TableHead className="py-1">Status</TableHead>
                            <TableHead className="py-1">Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(sendResults.results || []).sort((a, b) => (a.device_name || '').localeCompare(b.device_name || '')).map((r, i) => (
                            <TableRow key={i} className="h-8">
                              <TableCell className="font-medium py-1">{formatCommandDeviceLabel({ device_imei: r.imei, device_name: r.device_name, asset_name: r.asset_name, unit_number: r.unit_number })}</TableCell>
                              <TableCell className="text-xs font-mono py-1">{r.imei}</TableCell>
                              <TableCell className="py-1">
                                <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_BADGE[r.status] || 'bg-gray-100'}`}>{r.status}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground py-1">
                                {r.reasons ? r.reasons.join('; ') : r.error || r.delivery_method || ''}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {sendResults.sent > 0 && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          onClick={() => handleSend(false)}
                          className="bg-green-600 hover:bg-green-700 text-white gap-2"
                          disabled={sending}
                          data-testid="confirm-send-btn"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Confirm & Send {sendResults.sent} Command(s)
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Select Devices */}
              <Card>
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Radio className="w-3.5 h-3.5" />
                      Select Devices ({selectedDeviceIds.length} selected)
                      {activeProtocol && (
                        <Badge className={`text-[10px] h-4 px-1.5 py-0 leading-none ${activeProtocol === 'Meitrack' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>{activeProtocol} only</Badge>
                      )}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 pt-0 px-4 pb-3">
                  <div className="flex gap-2 flex-wrap items-center">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search name or IMEI..."
                        value={sendSearch}
                        onChange={e => setSendSearch(e.target.value)}
                        className="w-full pl-8 pr-7 h-7 text-xs dark:bg-[#0D1425]"
                        data-testid="search-devices"
                      />
                      {sendSearch && <button onClick={() => setSendSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                    <div className="w-36">
                      <Select value={sendProtocolFilter} onValueChange={v => setSendProtocolFilter(v === 'all_protocols' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs dark:bg-[#172031]" data-testid="filter-protocol"><SelectValue placeholder="Protocol" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_protocols">All Protocols</SelectItem>
                          {PROTOCOLS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <Select value={sendStatusFilter} onValueChange={v => setSendStatusFilter(v === 'all_statuses' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs dark:bg-[#172031]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_statuses">All Statuses</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="moving">Moving</SelectItem>
                          <SelectItem value="idle">Idle</SelectItem>
                          <SelectItem value="stopped">Stopped</SelectItem>
                          <SelectItem value="offline">Offline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Searchable user filter (Analytics > History pattern) — scales to 1000s of users. */}
                    <div className="w-44">
                      <Popover open={sendUserDropdownOpen} onOpenChange={setSendUserDropdownOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full h-7 px-2 justify-between text-xs font-normal dark:bg-[#172031]" data-testid="filter-user">
                            <span className="truncate">{sendUserFilterLabel}</span>
                            <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                              {sendUserFilter && (
                                <span
                                  role="button"
                                  onClick={(e) => { e.stopPropagation(); setSendUserFilter(''); }}
                                  className="text-muted-foreground hover:text-foreground"
                                  data-testid="filter-user-clear"
                                >
                                  <X className="w-3 h-3" />
                                </span>
                              )}
                              <Search className="w-3 h-3 opacity-50" />
                            </div>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[260px] p-0" align="start">
                          <div className="p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                              <Input
                                placeholder="Search by name / email..."
                                value={sendUserSearch}
                                onChange={(e) => setSendUserSearch(e.target.value)}
                                className="h-8 pl-8 pr-8 text-base md:text-sm"
                                autoFocus
                                data-testid="filter-user-search"
                              />
                              {sendUserSearch && (
                                <button onClick={() => setSendUserSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="max-h-[220px] overflow-y-auto" data-testid="filter-user-options">
                            {(() => {
                              const s = sendUserSearch.toLowerCase();
                              const options = [
                                { value: '', label: 'All Users', email: '' },
                                { value: 'not_allocated', label: 'Not Allocated', email: '' },
                                ...allocatedUserOptions,
                              ].filter(o => !s || o.label.toLowerCase().includes(s) || o.email.toLowerCase().includes(s));
                              if (options.length === 0) {
                                return <div className="p-4 text-center text-sm text-muted-foreground">No users found</div>;
                              }
                              return options.map(o => (
                                <button
                                  key={o.value || 'all_users'}
                                  className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${(sendUserFilter || '') === o.value ? 'bg-[#2563eb]/10 text-[#2563eb] font-medium' : ''}`}
                                  onClick={() => { setSendUserFilter(o.value); setSendUserDropdownOpen(false); setSendUserSearch(''); }}
                                  data-testid={`filter-user-option-${o.value || 'all_users'}`}
                                >
                                  {o.label}
                                </button>
                              ));
                            })()}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* iter563 — Selection bar (Audit Log parity). Appears only
                      when at least one device is ticked. */}
                  <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="send-devices-selection-bar">
                      <span data-testid="send-devices-selection-count">{selectedDeviceIds.length} selected</span>
{" "}
                  {selectedDeviceIds.length > 0 && (<>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={selectAllFilteredDevices}
                        data-testid="send-devices-select-all-toggle"
                      >
                        {selectedDeviceIds.length >= sameProtocolDevices.length && sameProtocolDevices.length > 0
                          ? 'Deselect All'
                          : `Select All ${selectionProtocol || ''} (${sameProtocolDevices.length})`.replace('  ', ' ')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => updateSelectedDevices([])}
                        data-testid="send-devices-selection-clear"
                      >
                        Clear
                      </Button>
                    </>)}
                  </div>

                  {selectAllDisabled && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 px-1" data-testid="send-devices-mixed-protocol-note">
                      {visiblePageProtocols.length > 1
                        ? 'Multiple protocols are visible — select a protocol in the Protocol dropdown first to enable Select All.'
                        : `Your current selection is ${selectionProtocol} — this page shows ${visiblePageProtocols[0]} devices, so Select All is disabled.`}
                    </p>
                  )}

                  <div className="border rounded-md">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow className="h-8">
                          <TableHead className="w-7 py-1">
                            {/* Header checkbox scopes to the visible page; disabled
                                when the page mixes protocols (single-protocol rule). */}
                            <Checkbox
                              checked={!selectAllDisabled && visiblePageDevices.length > 0 && visiblePageDevices.every(d => selectedDeviceIds.includes(d.id))}
                              onCheckedChange={toggleAllDevices}
                              disabled={selectAllDisabled}
                              className="h-3.5 w-3.5"
                              data-testid="select-all-devices"
                              aria-label="Select all devices on this page"
                              title={selectAllDisabled ? 'Select a protocol in the Protocol dropdown first' : undefined}
                            />
                          </TableHead>
                          <TableHead className="py-1">Device</TableHead>
                          <TableHead className="py-1">IMEI</TableHead>
                          <TableHead className="py-1">User</TableHead>
                          <TableHead className="py-1">Protocol</TableHead>
                          <TableHead className="py-1">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDevices.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4 text-xs">No devices match filters</TableCell></TableRow>
                        ) : visiblePageDevices.map(d => {
                          const rowDisabled = !!(selectionProtocol && d.protocol !== selectionProtocol);
                          return (
                          <TableRow key={d.id} className={`h-8 ${rowDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                            onClick={() => {
                              if (rowDisabled) { toast.info(`Selection is limited to ${selectionProtocol} devices`); return; }
                              updateSelectedDevices(
                                selectedDeviceIds.includes(d.id)
                                  ? selectedDeviceIds.filter(id => id !== d.id)
                                  : [...selectedDeviceIds, d.id]
                              );
                            }}
                          >
                            <TableCell className="py-1">
                              <Checkbox
                                checked={selectedDeviceIds.includes(d.id)}
                                disabled={rowDisabled}
                                onCheckedChange={(checked) => updateSelectedDevices(
                                  checked
                                    ? [...selectedDeviceIds, d.id]
                                    : selectedDeviceIds.filter(id => id !== d.id)
                                )}
                                className="h-3.5 w-3.5"
                                data-testid={`device-check-${d.id}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium py-1">{formatAssetLabel(d)}</TableCell>
                            <TableCell className="text-xs font-mono py-1">{d.imei}</TableCell>
                            {/* iter561 — allocated user column */}
                            <TableCell className="py-1" data-testid={`device-user-${d.id}`}>
                              {(() => {
                                const allocated = d.allocated_tenant_ids || [];
                                if (allocated.length === 0) {
                                  return <span className="text-[10px] text-muted-foreground italic">Not Allocated</span>;
                                }
                                const labels = allocated
                                  .map(uid => userMap[uid])
                                  .filter(Boolean)
                                  .map(u => u.name || u.email);
                                if (labels.length === 0) return <span className="text-[10px] text-muted-foreground italic">Not Allocated</span>;
                                return (
                                  <span className="text-[11px] truncate block max-w-[160px]" title={labels.join(', ')}>
                                    {labels[0]}{labels.length > 1 ? ` +${labels.length - 1}` : ''}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="py-1"><Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${d.protocol === 'Teltonika' ? 'border-blue-400 text-blue-600 dark:border-blue-500 dark:text-blue-400' : d.protocol === 'Meitrack' ? 'border-green-400 text-green-600 dark:border-green-500 dark:text-green-400' : ''}`}>{d.protocol}</Badge></TableCell>
                            <TableCell className="py-1">{getDeviceStatusBadge(d.status, d.id)}</TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <PaginationFooter
                    page={sendDevicesPage}
                    setPage={setSendDevicesPage}
                    totalItems={filteredDevices.length}
                    itemsPerPage={sendDevicesPageSize}
                    setItemsPerPage={setSendDevicesPageSize}
                    testIdPrefix="send-devices"
                    alwaysShow
                  />
                </CardContent>
              </Card>

              {/* Recent Replies Table */}
              <Card data-testid="recent-replies">
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <History className="w-3.5 h-3.5 text-blue-500" />
                      Recent Replies (Last 10)
                    </span>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="text-xs gap-1 h-6 px-2"
                        onClick={async () => {
                          try {
                            const res = await api.post('/admin/commands/check-sms-replies');
                            if (res.data.new > 0) toast.success(`${res.data.new} new SMS replies fetched`);
                            else toast.info('No new SMS replies');
                            fetchRecentReplies();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to check replies'); }
                        }}
                        data-testid="check-sms-replies"
                      >
                        <MessageSquare className="w-3 h-3" /> Check SMS Replies
                      </Button>
                      <Button variant="ghost" size="sm" onClick={fetchRecentReplies} data-testid="refresh-replies">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 px-4 pb-3">
                  <div className="border rounded-md">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow className="h-8">
                          <TableHead className="py-1">Time</TableHead>
                          <TableHead className="py-1">Device</TableHead>
                          <TableHead className="py-1">Command</TableHead>
                          <TableHead className="py-1">Method</TableHead>
                          <TableHead className="py-1">Status</TableHead>
                          <TableHead className="py-1">Response</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recentReplies.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4 text-xs">No recent replies</TableCell></TableRow>
                        ) : recentReplies.map((cmd, i) => (
                          <React.Fragment key={cmd.id || i}>
                            <TableRow className="cursor-pointer hover:bg-muted/50 h-8" onClick={() => setDetailDialog(cmd)}>
                              <TableCell className="text-[10px] py-1">{formatDate(cmd.created_at)}</TableCell>
                              <TableCell className="text-xs font-medium py-1">{formatCommandDeviceLabel(cmd)}</TableCell>
                              <TableCell className="text-[10px] font-mono max-w-[120px] truncate py-1">{cmd.command}</TableCell>
                              <TableCell className="py-1">
                                <Badge variant="outline" className="text-[9px] px-1 py-0">{cmd.delivery_method || 'gprs'}</Badge>
                              </TableCell>
                              <TableCell className="py-1">
                                <Badge className={`text-[9px] px-1 py-0 ${STATUS_BADGE[cmd.status] || 'bg-gray-100'}`}>{cmd.status}</Badge>
                              </TableCell>
                              <TableCell className="text-[10px] py-1">
                                {cmd.response || cmd.error
                                  ? <span className="text-green-700 dark:text-green-500 font-medium text-[10px]">Reply</span>
                                  : <span className="text-muted-foreground italic">Awaiting...</span>}
                              </TableCell>
                            </TableRow>
                            {(cmd.response || cmd.error) && (
                              <TableRow>
                                <TableCell colSpan={6} className="py-1.5 px-4 border-b-0">
                                  <div className="text-xs font-mono whitespace-pre-wrap break-all text-green-700 dark:text-green-400">{linkifyText(cmd.response || cmd.error)}</div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════ TEMPLATES SECTION ═══════════════════════ */}
      {activeSection === 'templates' && (
        <div className="space-y-4" data-testid="templates-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-semibold">Command Templates</h3>
          </div>

          {/* Full-width controls row — search + filters + action buttons together */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search name, description, command..."
                value={templateSearch}
                onChange={e => setTemplateSearch(e.target.value)}
                className="w-full h-8 pl-10 pr-8 dark:bg-[#0D1425]"
                data-testid="template-search"
              />
              {templateSearch && <button onClick={() => setTemplateSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
            </div>
            <div className="w-44">
              <Select value={templateProtocolFilter} onValueChange={v => setTemplateProtocolFilter(v === 'all_template_protocols' ? '' : v)}>
                <SelectTrigger className="h-8 dark:bg-[#0D1425]" data-testid="template-filter-protocol"><SelectValue placeholder="All Protocols" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_template_protocols">All Protocols</SelectItem>
                  {[...PROTOCOLS].sort().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <input type="file" accept=".json" onChange={handleImportTemplates} className="hidden" id="template-json-import" />
            <Button size="sm" variant="outline" onClick={() => document.getElementById('template-json-import').click()} data-testid="import-templates-btn">
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportTemplates} disabled={selectedTemplateExportIds.length === 0} data-testid="export-templates-btn">
              <Upload className="w-4 h-4 mr-2" />
              {selectedTemplateExportIds.length > 0 ? `Export (${selectedTemplateExportIds.length})` : 'Export'}
            </Button>
            <Button size="sm" onClick={openNewTemplate} className="gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="create-template-btn">
              <Plus className="w-4 h-4" /> New Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No templates yet. Create your first command template.</p>
            </Card>
          ) : (() => {
            const searchLower = templateSearch.toLowerCase();
            const filtered = templates
              .filter(t => !templateProtocolFilter || t.protocol === templateProtocolFilter)
              .filter(t => !searchLower || (t.name || '').toLowerCase().includes(searchLower) || (t.description || '').toLowerCase().includes(searchLower) || (t.command_text || '').toLowerCase().includes(searchLower));
            // Group by protocol (preserve DB sort_order, no alphabetical sort)
            const grouped = {};
            filtered.forEach(t => {
              const proto = t.protocol || 'Unknown';
              if (!grouped[proto]) grouped[proto] = [];
              grouped[proto].push(t);
            });
            // Sort groups by saved order, then append any new protocols at end
            const knownOrder = protocolGroupOrder.filter(p => grouped[p]);
            const unknownProtos = Object.keys(grouped).filter(p => !knownOrder.includes(p));
            const orderedProtocols = [...knownOrder, ...unknownProtos];
            // Sync group order if new protocols appeared
            if (unknownProtos.length > 0 && protocolGroupOrder.length > 0) {
              setProtocolGroupOrder(orderedProtocols);
              api.put('/admin/commands/templates/group-order', { order: orderedProtocols }).catch(() => {});
            } else if (protocolGroupOrder.length === 0 && orderedProtocols.length > 0) {
              setProtocolGroupOrder(orderedProtocols);
            }

            if (filtered.length === 0) {
              return (
                <Card className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No templates match the selected filter.</p>
                </Card>
              );
            }

            const allFilteredIds = filtered.map(t => t.id);
            const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedTemplateExportIds.includes(id));

            return (
              <>
              {selectedTemplateExportIds.length > 0 && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{selectedTemplateExportIds.length} selected</span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => setSelectedTemplateExportIds(allSelected ? [] : allFilteredIds)}
                    data-testid="toggle-select-all-templates"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => setSelectedTemplateExportIds([])}
                  >
                    Clear
                  </Button>
                </div>
              )}
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(event) => {
                // Determine if this is a group drag or card drag
                const { active } = event;
                if (orderedProtocols.includes(active.id)) {
                  handleGroupDragEnd(event);
                } else {
                  handleTemplateDragEnd(event);
                }
              }}>
              <SortableContext items={orderedProtocols} strategy={verticalListSortingStrategy}>
              {orderedProtocols.map(proto => (
              <div key={proto} className="space-y-3 pb-2" data-testid={`template-group-${proto}`}>
                <SortableProtocolGroup id={proto}>
                    <Badge variant="outline" className={`text-xs font-semibold px-2.5 py-1 ${
                      proto === 'Teltonika' ? 'border-blue-400 text-blue-600 dark:border-blue-500 dark:text-blue-400' :
                      proto === 'Meitrack' ? 'border-green-400 text-green-600 dark:border-green-500 dark:text-green-400' :
                      'border-gray-400 text-gray-600'
                    }`}>{proto}</Badge>
                    <span className="text-xs text-muted-foreground">{grouped[proto].length} template{grouped[proto].length !== 1 ? 's' : ''}</span>
                    <div className="flex-1 h-px bg-border" />
                </SortableProtocolGroup>
                <SortableContext items={grouped[proto].map(t => t.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {grouped[proto].map(t => (
                    <SortableTemplateCard key={t.id} id={t.id}>
                    <Card className={`group h-full ${CARD_COLOR_MAP[t.button_color] || CARD_COLOR_MAP.blue} ${selectedTemplateExportIds.includes(t.id) ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`} data-testid={`template-card-${t.id}`}>
                      <CardContent className="p-2.5 pl-7 space-y-1.5">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-1.5">
                            <Checkbox
                              checked={selectedTemplateExportIds.includes(t.id)}
                              onCheckedChange={(checked) => setSelectedTemplateExportIds(prev =>
                                checked ? [...prev, t.id] : prev.filter(id => id !== t.id)
                              )}
                              className="h-3.5 w-3.5"
                              data-testid={`template-select-${t.id}`}
                            />
                            <div className={`w-2.5 h-2.5 rounded-full ${BTN_COLOR_MAP[t.button_color]?.split(' ')[0] || 'bg-blue-600'}`} />
                            <h4 className="font-semibold text-xs leading-tight">{t.name}</h4>
                          </div>
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => openEditTemplate(t)} title="Edit" data-testid={`edit-template-${t.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteTemplate(t)} title="Delete" data-testid={`delete-template-${t.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-0.5">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">{t.category}</Badge>
                          <Badge className={`text-[9px] px-1.5 py-0 ${RISK_COLORS[t.risk_level]}`}>{t.risk_level}</Badge>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">{t.preferred_delivery_method === 'sms' ? 'SMS' : t.preferred_delivery_method === 'sms_3rdparty' ? (activeSmsProvider === 'mobilemessage' ? 'MM' : 'Twilio') : 'GPRS'}</Badge>
                          {t.command_mode === 'sequence' && <Badge variant="outline" className="text-[9px] px-1.5 py-0">Seq</Badge>}
                        </div>
                        <p className="text-[10px] font-mono bg-muted/50 p-1 rounded truncate">
                          {t.command_mode === 'sequence' ? `${(t.command_sequence || []).length} steps` : t.command_text}
                        </p>
                        {t.description && <p className="text-[10px] text-muted-foreground line-clamp-1">{t.description}</p>}
                        {(t.preconditions?.require_online || t.preconditions?.require_speed_zero || t.preconditions?.require_ignition_off || t.preconditions?.require_ignition_on || t.preconditions?.max_last_seen_minutes) && (
                          <div className="flex flex-wrap gap-0.5">
                            {t.preconditions.require_online && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Online</Badge>}
                            {t.preconditions.require_speed_zero && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Speed=0</Badge>}
                            {t.preconditions.require_ignition_off && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Ign OFF</Badge>}
                            {t.preconditions.require_ignition_on && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Ign ON</Badge>}
                            {t.preconditions.max_last_seen_minutes && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">&lt;{t.preconditions.max_last_seen_minutes}m</Badge>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    </SortableTemplateCard>
                  ))}
                </div>
                </SortableContext>
              </div>
            ))}
            </SortableContext>
            </DndContext>
            </>
            );
          })()}
        </div>
      )}

      {/* ═══════════════════════ HISTORY SECTION ═══════════════════════ */}
      {activeSection === 'history' && (
        <div className="space-y-4" data-testid="history-section">

          {/* iter151 — Device Health card: surfaces the slowest device
              replies + YYMM rescue counts over the last 7 days so admins
              can spot "slow GPRS link" devices AND "buggy firmware" devices
              in one place, without remembering journalctl/Mongo queries. */}
          <div className="rounded-lg border bg-card shadow-sm" data-testid="device-health-card">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <div className="flex flex-col">
                  <h4 className="text-sm font-semibold leading-tight">Device Health · Last 7 Days</h4>
                  <span className="text-[11px] text-muted-foreground">
                    Top {Math.min(10, healthDevices.length || 10)} slowest by avg reply latency
                    {healthGeneratedAt && (
                      <> · refreshed {formatDate(healthGeneratedAt)}</>
                    )}
                  </span>
                </div>
              </div>
              <Button
                variant="outline" size="sm" className="h-8 text-xs gap-1"
                onClick={fetchDeviceHealth}
                disabled={healthLoading}
                data-testid="device-health-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${healthLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="px-4 py-2">
              {healthDevices.length === 0 && !healthLoading && (
                <div className="text-xs text-muted-foreground py-4 text-center" data-testid="device-health-empty">
                  No device replies in the last 7 days yet.
                </div>
              )}
              {healthDevices.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="font-medium py-2 pr-2 w-8">#</th>
                        <th className="font-medium py-2 pr-2">Device</th>
                        <th className="font-medium py-2 pr-2 hidden md:table-cell">IMEI</th>
                        <th className="font-medium py-2 pr-2 text-right">Avg / Max</th>
                        <th className="font-medium py-2 pr-2 text-right hidden sm:table-cell">Cmds</th>
                        <th className="font-medium py-2 pr-2 text-right" title="Percentage of commands sent that never received a device reply within the retention window">No Reply %</th>
                        <th className="font-medium py-2 pr-2 text-right">YYMM Rescues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {healthDevices.map((d, idx) => {
                        const avgS = (d.avg_latency_ms / 1000);
                        const maxS = (d.max_latency_ms / 1000);
                        const isSlow = avgS >= 10;
                        const hasBug = d.fallback_rescues_attribution_mismatch > 0;
                        // iter151 follow-up — % of commands sent that
                        // never got a reply (effectively the "expired"
                        // rate). >25% = red (probably unreachable);
                        // 10-25% = amber (intermittent); <10% = plain.
                        const expiredPct = (d.expired_rate || 0) * 100;
                        const expiredCls = expiredPct >= 25
                          ? 'text-red-700 dark:text-red-400 font-semibold'
                          : expiredPct >= 10
                            ? 'text-amber-700 dark:text-amber-400 font-medium'
                            : 'text-muted-foreground';
                        return (
                          <tr
                            key={d.imei}
                            className={`border-b last:border-0 hover:bg-muted/40 ${d.device_id ? 'cursor-pointer' : ''}`}
                            onClick={() => d.device_id && window.location.assign(`/admin-panel?tab=devices&device_id=${d.device_id}`)}
                            data-testid={`device-health-row-${idx}`}
                          >
                            <td className="py-1.5 pr-2 text-muted-foreground tabular-nums">{idx + 1}</td>
                            <td className="py-1.5 pr-2 truncate max-w-[260px]" title={formatAssetLabel(d)}>
                              <span className={isSlow ? 'font-semibold text-amber-700 dark:text-amber-400' : 'font-medium'}>
                                {formatAssetLabel(d)}
                              </span>
                            </td>
                            <td className="py-1.5 pr-2 font-mono text-[11px] text-muted-foreground hidden md:table-cell">{d.imei}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                              <span className={isSlow ? 'text-amber-700 dark:text-amber-400 font-semibold' : ''}>
                                {avgS < 1 ? `${(avgS * 1000).toFixed(0)}ms` : `${avgS.toFixed(1)}s`}
                              </span>
                              <span className="text-muted-foreground"> / {maxS < 1 ? `${(maxS * 1000).toFixed(0)}ms` : `${maxS.toFixed(1)}s`}</span>
                            </td>
                            <td className="py-1.5 pr-2 text-right text-muted-foreground tabular-nums hidden sm:table-cell">{d.total_commands}</td>
                            <td
                              className={`py-1.5 pr-2 text-right tabular-nums ${expiredCls}`}
                              title={`${d.expired_count || 0} of ${d.total_commands || 0} commands never replied`}
                              data-testid={`device-health-noreply-${idx}`}
                            >
                              {expiredPct >= 1 ? `${expiredPct.toFixed(0)}%` : expiredPct > 0 ? '<1%' : '0%'}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                              {hasBug ? (
                                <span
                                  className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 font-semibold"
                                  title={`${d.fallback_rescues_attribution_mismatch} TCP attribution mismatch event(s) — possible firmware bug`}
                                >
                                  <AlertCircle className="w-3 h-3" />
                                  {d.fallback_rescues_attribution_mismatch} bug
                                </span>
                              ) : d.fallback_rescues_race > 0 ? (
                                <span
                                  className="text-muted-foreground"
                                  title={`${d.fallback_rescues_race} benign race(s) — no action needed`}
                                >
                                  {d.fallback_rescues_race} race
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-semibold">Command History</h3>
          </div>

          {/* Full-width controls row — search + filters + action buttons together */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search device, IMEI, command..."
                value={historySearch}
                onChange={e => { setHistorySearch(e.target.value); setHistoryPage(0); }}
                className="w-full h-8 pl-10 pr-8 dark:bg-[#0D1425]"
                data-testid="history-search"
              />
              {historySearch && <button onClick={() => { setHistorySearch(''); setHistoryPage(0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
            </div>
            <Select value={historyFilter.status} onValueChange={v => { setHistoryFilter(p => ({ ...p, status: v === 'all_history_statuses' ? '' : v })); setHistoryPage(0); }}>
              <SelectTrigger className="h-8 w-36 dark:bg-[#0D1425]" data-testid="history-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_history_statuses">All Statuses</SelectItem>
                {['queued', 'sending', 'sent', 'acknowledged', 'failed', 'timeout', 'expired'].map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
                <SelectItem value="no_response">No Response</SelectItem>
              </SelectContent>
            </Select>
            <Select value={historyFilter.delivery_method} onValueChange={v => { setHistoryFilter(p => ({ ...p, delivery_method: v === 'all_delivery_methods' ? '' : v })); setHistoryPage(0); }}>
              <SelectTrigger className="h-8 w-36 dark:bg-[#0D1425]" data-testid="history-filter-delivery"><SelectValue placeholder="All Methods" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_delivery_methods">All Methods</SelectItem>
                <SelectItem value="gprs">GPRS</SelectItem>
                <SelectItem value="sms_api">SMS API</SelectItem>
                <SelectItem value="sms_3p">SMS 3rd Party</SelectItem>
              </SelectContent>
            </Select>
            {/* Date range — Event Log "All Time" preset filter (shared component). */}
            <DateRangePresetFilter value={historyDateRange} onChange={(r) => { setHistoryDateRange(r); setHistoryPage(0); }} testIdPrefix="history-date" resetSignal={historyDateResetKey} triggerClassName="dark:bg-[#0D1425]" />
            <Button variant="outline" size="sm" className="text-xs gap-1"
              onClick={async () => {
                try {
                  const res = await api.post('/admin/commands/check-sms-replies');
                  if (res.data.new > 0) { toast.success(`${res.data.new} new SMS replies fetched`); fetchHistory(historyPage); }
                  else toast.info('No new SMS replies');
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to check replies'); }
              }}
              data-testid="history-check-sms-replies"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Check SMS Replies
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportHistory} disabled={selectedHistoryIds.length === 0} data-testid="export-history-btn">
              <Upload className="w-4 h-4 mr-2" />
              {selectedHistoryIds.length > 0 ? `Export (${selectedHistoryIds.length})` : 'Export'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setHistoryDateRange({ from: undefined, to: undefined }); setHistoryDateResetKey(k => k + 1); setHistoryPage(0); fetchHistory(0); }} data-testid="refresh-history">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="command-history-selection-bar">
              <span data-testid="command-history-selection-count">{selectedHistoryIds.length} selected</span>
          {selectedHistoryIds.length > 0 && (<>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                data-testid="command-history-select-all-toggle"
                disabled={historySelectAllLoading}
                onClick={async () => {
                  // Same rule as the label — never disagree (Driver Corrections fix).
                  if (selectedHistoryIds.length >= historyTotal) {
                    setSelectedHistoryIds([]);
                    return;
                  }
                  try {
                    setHistorySelectAllLoading(true);
                    const params = new URLSearchParams();
                    if (historyFilter.status) params.set('status', historyFilter.status);
                    if (historyFilter.delivery_method) params.set('delivery_method', historyFilter.delivery_method);
                    if (historySearch) params.set('search', historySearch);
                    if (historyDateRange.from) params.set('from_date', historyDateRange.from.toISOString());
                    if (historyDateRange.to) {
                                            params.set('to_date', historyDateRange.to.toISOString());
                    }
                    const res = await api.get(`/admin/commands/history/export?${params}`);
                    setSelectedHistoryIds((res.data.records || []).map(r => r.id).filter(Boolean));
                  } catch {
                    toast.error('Failed to select all rows');
                  } finally {
                    setHistorySelectAllLoading(false);
                  }
                }}
              >
                {historySelectAllLoading ? 'Selecting…' : (selectedHistoryIds.length >= historyTotal ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedHistoryIds([])}>Clear</Button>
            </>)}
          </div>

          <Card>
            <CardContent className="p-0">
              <SyncedTableScroll minWidth="1944px" testId="command-history-table-scroll">
                <Table className="table-fixed" wrapperClassName="overflow-visible">
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={history.filter(c => c.id).length > 0 && history.filter(c => c.id).every(c => selectedHistoryIds.includes(c.id))}
                          onCheckedChange={(checked) => {
                            const pageIds = history.map(c => c.id).filter(Boolean);
                            setSelectedHistoryIds(prev => checked
                              ? Array.from(new Set([...prev, ...pageIds]))
                              : prev.filter(id => !pageIds.includes(id)));
                          }}
                        />
                      </TableHead>
                      <TableHead
                        className="w-36 whitespace-nowrap cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => cycleHistorySort('created_at')}
                        data-testid="command-history-sort-time"
                        title="Click to sort by time (3 clicks cycle: desc → asc → default)"
                      >
                        <div className="flex items-center gap-1">
                          <span>Time</span>
                          <span className="text-xs opacity-60 tabular-nums">
                            {historySort.field === 'created_at' ? (historySort.dir === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </div>
                      </TableHead>
                      <TableHead className="w-44 whitespace-nowrap">Device Name</TableHead>
                      <TableHead className="w-36 whitespace-nowrap">IMEI</TableHead>
                      <TableHead className="w-40 whitespace-nowrap">ICCID</TableHead>
                      <TableHead className="w-36 whitespace-nowrap">Mobile Number</TableHead>
                      <TableHead className="w-96 whitespace-nowrap">Command</TableHead>
                      <TableHead className="w-24 whitespace-nowrap">Method</TableHead>
                      <TableHead className="w-32 whitespace-nowrap">Status</TableHead>
                      <TableHead
                        className="w-52 whitespace-nowrap cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => cycleHistorySort('latency')}
                        data-testid="command-history-replied-header"
                        title="Click to sort by reply latency (slowest first → fastest first → default order)"
                      >
                        <div className="flex items-center gap-1" data-testid="command-history-sort-latency">
                          <span>Replied</span>
                          <span className="text-[10px] text-muted-foreground">(latency)</span>
                          <span className="text-xs opacity-60 tabular-nums">
                            {historySort.field === 'latency' ? (historySort.dir === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </div>
                      </TableHead>
                      <TableHead className="w-80 whitespace-nowrap">Response</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedHistory.length === 0 ? (
                      <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No commands found</TableCell></TableRow>
                    ) : sortedHistory.map((cmd, i) => (
                      <TableRow key={cmd.id || i} className={`cursor-pointer hover:bg-muted/50 ${selectedHistoryIds.includes(cmd.id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`} onClick={() => setDetailDialog(cmd)}>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedHistoryIds.includes(cmd.id)}
                            onCheckedChange={(checked) => setSelectedHistoryIds(prev =>
                              checked ? [...prev, cmd.id] : prev.filter(id => id !== cmd.id)
                            )}
                          />
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(cmd.created_at)}</TableCell>
                        <TableCell className="text-xs font-medium truncate" title={formatCommandDeviceLabel(cmd)} data-testid="history-row-device">{formatCommandDeviceLabel(cmd)}</TableCell>
                        <TableCell className="text-xs font-mono truncate" title={cmd.device_imei || '-'}>{cmd.device_imei || '-'}</TableCell>
                        <TableCell className="text-xs font-mono truncate" title={cmd.iccid || '-'}>{cmd.iccid || '-'}</TableCell>
                        <TableCell className="text-xs truncate" title={cmd.mobile_number || '-'}>{cmd.mobile_number || '-'}</TableCell>
                        <TableCell className="text-xs font-mono truncate" title={cmd.command || cmd.message || '-'}>{cmd.command || cmd.message || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{cmd.delivery_method || 'gprs'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <Badge className={`text-[10px] w-fit ${STATUS_BADGE[cmd.status] || 'bg-gray-100'}`}>{cmd.status}</Badge>
                            {(cmd.sequence_total > 0) && (
                              <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap" data-testid={`history-seq-progress-${cmd.id}`}>
                                Step {(cmd.sequence_progress || []).filter(p => p.status === 'sent').length}/{cmd.sequence_total}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          className="text-xs whitespace-nowrap truncate"
                          title={cmd.responded_at ? `${formatDate(cmd.responded_at)} • latency ${formatReplyLatency(cmd)}` : '-'}
                          data-testid="command-history-replied-cell"
                        >
                          {cmd.responded_at ? (
                            <span className="inline-flex items-baseline gap-1.5">
                              <span>{formatDate(cmd.responded_at)}</span>
                              <span className="text-[10px] text-muted-foreground tabular-nums">({formatReplyLatency(cmd)})</span>
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-xs truncate" title={cmd.response || cmd.error || '-'}>{linkifyText(cmd.response || cmd.error || '-')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SyncedTableScroll>
              {/* Pagination — INSIDE the Card so it sits attached to the table (matches
                  every other paginated table in the app). */}
              <PaginationFooter
                page={historyPage}
                setPage={setHistoryPage}
                totalItems={historyTotal}
                itemsPerPage={historyPageSize}
                setItemsPerPage={setHistoryPageSize}
                testIdPrefix="command-history"
                zeroIndexed
                alwaysShow
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════════ AUDIT LOG SECTION ═══════════════════════ */}
      {activeSection === 'audit' && (
        <div className="space-y-4" data-testid="audit-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-semibold flex items-center gap-2"><Shield className="w-5 h-5" /> Audit Log</h3>
          </div>

          {/* Full-width controls row — search + filters + action buttons together */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search action, user, role..."
                value={auditSearch}
                onChange={e => setAuditSearch(e.target.value)}
                className="w-full h-8 pl-10 pr-8 dark:bg-[#0D1425]"
                data-testid="audit-search"
              />
              {auditSearch && <button onClick={() => setAuditSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
            </div>
            {/* Date range — Event Log "All Time" preset filter (shared component). */}
            <DateRangePresetFilter value={auditDateRange} onChange={setAuditDateRange} testIdPrefix="audit-date" resetSignal={auditDateResetKey} triggerClassName="dark:bg-[#0D1425]" />
            <Button size="sm" variant="outline" onClick={handleExportAuditLog} disabled={selectedAuditIds.length === 0} data-testid="export-audit-btn">
              <Upload className="w-4 h-4 mr-2" />
              {selectedAuditIds.length > 0 ? `Export (${selectedAuditIds.length})` : 'Export'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setAuditDateRange({ from: undefined, to: undefined }); setAuditDateResetKey(k => k + 1); fetchAuditLog(); }} data-testid="refresh-audit">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="audit-log-selection-bar">
              <span data-testid="command-audit-selection-count">{selectedAuditIds.length} selected</span>
          {selectedAuditIds.length > 0 && (<>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                data-testid="command-audit-select-all-toggle"
                disabled={auditSelectAllLoading}
                onClick={async () => {
                  if (selectedAuditIds.length >= auditTotal) {
                    setSelectedAuditIds([]);
                    return;
                  }
                  try {
                    setAuditSelectAllLoading(true);
                    const params = new URLSearchParams();
                    if (auditSearch) params.set('search', auditSearch);
                    if (auditDateRange.from) params.set('from_date', auditDateRange.from.toISOString());
                    if (auditDateRange.to) {
                                            params.set('to_date', auditDateRange.to.toISOString());
                    }
                    const res = await api.get(`/admin/commands/audit-log/export?${params}`);
                    setSelectedAuditIds((res.data.entries || []).map(e => e.id).filter(Boolean));
                  } catch {
                    toast.error('Failed to select all rows');
                  } finally {
                    setAuditSelectAllLoading(false);
                  }
                }}
              >
                {auditSelectAllLoading ? 'Selecting…' : (selectedAuditIds.length >= auditTotal ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedAuditIds([])}>Clear</Button>
            </>)}
          </div>

          <Card>
            <CardContent className="p-0">
              <SyncedTableScroll minWidth="1744px" testId="command-audit-table-scroll">
                <Table className="table-fixed" wrapperClassName="overflow-visible">
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={auditLog.filter(e => e.id).length > 0 && auditLog.filter(e => e.id).every(e => selectedAuditIds.includes(e.id))}
                          onCheckedChange={(checked) => {
                            const pageIds = auditLog.map(e => e.id).filter(Boolean);
                            setSelectedAuditIds(prev => checked
                              ? Array.from(new Set([...prev, ...pageIds]))
                              : prev.filter(id => !pageIds.includes(id)));
                          }}
                        />
                      </TableHead>
                      <TableHead className="w-36">Time</TableHead>
                      <TableHead className="w-44">Action</TableHead>
                      <TableHead className="w-56">User</TableHead>
                      <TableHead className="w-32">Role</TableHead>
                      <TableHead className="w-56">Device</TableHead>
                      <TableHead className="w-44">ICCID</TableHead>
                      <TableHead className="w-[520px]">Details</TableHead>
                      <TableHead className="w-28">Status @ Send</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLog.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No audit entries</TableCell></TableRow>
                    ) : auditLog.map((entry, i) => (
                      <TableRow key={entry.id || i} className={selectedAuditIds.includes(entry.id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedAuditIds.includes(entry.id)}
                            onCheckedChange={(checked) => setSelectedAuditIds(prev =>
                              checked ? [...prev, entry.id] : prev.filter(id => id !== entry.id)
                            )}
                          />
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(entry.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="max-w-full truncate text-[10px]">{(entry.action || '').replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-xs truncate" title={entry.user_email || '-'}>{entry.user_email}</TableCell>
                        <TableCell className="text-xs truncate" title={(entry.user_role || '').replace(/_/g, ' ')}>{(entry.user_role || '').replace(/_/g, ' ')}</TableCell>
                        <TableCell className="text-xs truncate" data-testid="audit-row-device">
                          {(() => {
                            const dr = entry.details?.device_results;
                            // iter151 Phase 3 — prefer pre-formatted
                            // hybrid labels stored at audit-time.
                            const labels = entry.details?.device_labels;
                            if (labels && labels.length > 0) {
                              if (labels.length <= 2) return labels.join(', ');
                              return `${labels[0]}, +${labels.length - 1} more`;
                            }
                            // Fallback for entries logged before Phase 3: build
                            // the hybrid label on the fly from device_results.
                            if (dr && dr.length > 0) {
                              const display = dr.map(formatAuditDeviceLabel).filter(Boolean);
                              if (display.length <= 2) return display.join(', ');
                              return `${display[0]}, +${display.length - 1} more`;
                            }
                            // Last-resort fallback for very old entries that
                            // only carry the legacy device_names array.
                            const names = entry.details?.device_names;
                            if (names && names.length > 0) {
                              if (names.length <= 2) return names.join(', ');
                              return `${names[0]}, +${names.length - 1} more`;
                            }
                            return '-';
                          })()}
                        </TableCell>
                        <TableCell className="text-xs font-mono truncate">
                          {(() => {
                            const dr = entry.details?.device_results;
                            if (dr && dr.length > 0) {
                              const iccids = dr.map(r => r.iccid).filter(Boolean);
                              if (iccids.length === 0) return '-';
                              if (iccids.length <= 2) return iccids.join(', ');
                              return `${iccids[0]}, +${iccids.length - 1} more`;
                            }
                            return '-';
                          })()}
                        </TableCell>
                        <TableCell className="text-xs truncate" title={JSON.stringify(entry.details || {})}>
                          {(() => {
                            const d = entry.details || {};
                            const parts = [];
                            if (d.template_id) parts.push(`Template: ${d.name || d.template_id}`);
                            if (d.custom_command) parts.push(`Command: ${d.custom_command}`);
                            if (d.delivery_method) parts.push(`Method: ${d.delivery_method}`);
                            if (d.device_count) parts.push(`Devices: ${d.device_count}`);
                            if (d.sent !== undefined) parts.push(`Sent: ${d.sent}`);
                            if (d.skipped) parts.push(`Skipped: ${d.skipped}`);
                            if (d.failed) parts.push(`Failed: ${d.failed}`);
                            if (d.provider) parts.push(`Provider: ${d.provider}`);
                            if (d.after?.name) parts.push(`Name: ${d.after.name}`);
                            if (d.after?.protocol) parts.push(`Protocol: ${d.after.protocol}`);
                            if (d.after?.risk_level) parts.push(`Risk: ${d.after.risk_level}`);
                            if (parts.length > 0) return parts.join(' | ');
                            const str = JSON.stringify(d);
                            return str.length > 120 ? str.slice(0, 120) + '...' : str;
                          })()}
                        </TableCell>
                        <TableCell className="text-xs">
                          {entry.action === 'commands_sent' && (entry.details?.device_results || []).some(r => r?.imei) ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400"
                              onClick={(e) => { e.stopPropagation(); openStateAtSendDialog(entry); }}
                              data-testid={`view-state-at-send-${i}`}
                              title="View device GPS, ignition, and IO state at the moment the command was sent"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SyncedTableScroll>
              {/* Pagination — INSIDE the Card. */}
              <PaginationFooter
                page={auditPage}
                setPage={setAuditPage}
                totalItems={auditTotal}
                itemsPerPage={auditPageSize}
                setItemsPerPage={setAuditPageSize}
                testIdPrefix="audit-log"
                zeroIndexed
                alwaysShow
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════════ TEMPLATE DIALOG ═══════════════════════ */}
      <ConfirmActionDialog
        open={!!deleteTemplateTarget}
        onOpenChange={(o) => { if (!o) setDeleteTemplateTarget(null); }}
        title="Delete Template?"
        message={deleteTemplateTarget?.name ? <>Delete the command template <b>{deleteTemplateTarget.name}</b>? This cannot be undone.</> : 'Delete this template? This cannot be undone.'}
        confirmLabel="Delete"
        onConfirm={doDeleteTemplate}
        testPrefix="delete-template-confirm"
      />

      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Command Template'}</DialogTitle>
            <DialogDescription>Define a reusable command with preconditions and risk level.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Row 1: Name + Protocol */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Get Device Info" data-testid="template-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Protocol <span className="text-red-500">*</span></Label>
                <Select value={templateForm.protocol} onValueChange={v => setTemplateForm(p => ({ ...p, protocol: v }))}>
                  <SelectTrigger data-testid="template-protocol-select"><SelectValue placeholder="Select Protocol" /></SelectTrigger>
                  <SelectContent>
                    {[...PROTOCOLS].sort().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Category + Risk + Color */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={templateForm.category} onValueChange={v => setTemplateForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Risk Level</Label>
                <Select value={templateForm.risk_level} onValueChange={v => setTemplateForm(p => ({ ...p, risk_level: v }))}>
                  <SelectTrigger data-testid="template-risk-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(r => <SelectItem key={r} value={r}><span className="capitalize">{r}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Button Color</Label>
                <Select value={templateForm.button_color} onValueChange={v => setTemplateForm(p => ({ ...p, button_color: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUTTON_COLORS.map(c => (
                      <SelectItem key={c} value={c}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${BTN_COLOR_MAP[c]?.split(' ')[0] || 'bg-gray-400'}`} />
                          <span className="capitalize">{c}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={templateForm.description} onChange={e => setTemplateForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional description..." />
            </div>

            {/* Preferred Delivery Method */}
            <div className="space-y-1">
              <Label>Preferred Delivery Method</Label>
              <Select value={templateForm.preferred_delivery_method || 'gprs'} onValueChange={v => setTemplateForm(p => ({ ...p, preferred_delivery_method: v }))}>
                <SelectTrigger data-testid="template-delivery-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_METHODS.map(dm => (
                    <SelectItem key={dm.value} value={dm.value}>
                      <span className="flex items-center gap-2"><dm.icon className="w-3.5 h-3.5" />{dm.label || (dm.value === 'sms_3rdparty' ? `SMS (3rd Party - ${activeSmsProvider === 'mobilemessage' ? 'MM' : 'Twilio'})` : dm.value)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Auto-selects this delivery method when this template is chosen</p>
            </div>

            {/* Command Mode */}
            <div className="space-y-2">
              <Label>Command Mode</Label>
              <div className="flex gap-2">
                <Button size="sm" variant={templateForm.command_mode === 'single' ? 'default' : 'outline'}
                  onClick={() => setTemplateForm(p => ({ ...p, command_mode: 'single' }))}
                  className={templateForm.command_mode === 'single' ? 'bg-[#2563eb]' : ''}
                >Single</Button>
                <Button size="sm" variant={templateForm.command_mode === 'sequence' ? 'default' : 'outline'}
                  onClick={() => setTemplateForm(p => ({ ...p, command_mode: 'sequence' }))}
                  className={templateForm.command_mode === 'sequence' ? 'bg-[#2563eb]' : ''}
                >Sequence</Button>
              </div>
            </div>

            {/* Single command */}
            {templateForm.command_mode === 'single' && (
              <div className="space-y-1">
                <Label>Command Text</Label>
                <Input value={templateForm.command_text} onChange={e => setTemplateForm(p => ({ ...p, command_text: e.target.value }))}
                  placeholder="e.g. getinfo" className="font-mono" data-testid="template-command-input" />
                <p className="text-[11px] text-muted-foreground">Enter the command only. Device PIN/password and protocol formatting are applied automatically when sending via SMS.</p>
              </div>
            )}

            {/* Sequence */}
            {templateForm.command_mode === 'sequence' && (
              <div className="space-y-2">
                <Label>Command Sequence</Label>
                {(templateForm.command_sequence || []).map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6">{i + 1}.</span>
                    <Input
                      value={step.command_text}
                      onChange={e => {
                        const seq = [...templateForm.command_sequence];
                        seq[i] = { ...seq[i], command_text: e.target.value };
                        setTemplateForm(p => ({ ...p, command_sequence: seq }));
                      }}
                      placeholder="Command text"
                      className="font-mono flex-1"
                    />
                    <div className="relative w-28">
                      <NumberInput
                        value={step.delay_ms}
                        onValueChange={(n) => {
                          const seq = [...templateForm.command_sequence];
                          seq[i] = { ...seq[i], delay_ms: n };
                          setTemplateForm(p => ({ ...p, command_sequence: seq }));
                        }}
                        min="0"
                        placeholder="Delay"
                        className="pr-9"
                        data-testid={`template-sequence-delay-${i}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none" data-testid={`template-sequence-delay-unit-${i}`}>ms</span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0"
                      onClick={() => setTemplateForm(p => ({ ...p, command_sequence: p.command_sequence.filter((_, j) => j !== i) }))}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setTemplateForm(p => ({
                  ...p, command_sequence: [...(p.command_sequence || []), { command_text: '', delay_ms: 0 }]
                }))}>
                  <Plus className="w-3 h-3 mr-1" /> Add Step
                </Button>
                <p className="text-[11px] text-muted-foreground">Delay is in milliseconds (ms) — wait time before sending the next command (e.g. 5000 = 5 seconds)</p>
              </div>
            )}

            {/* Preconditions */}
            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <Label className="flex items-center gap-2"><Shield className="w-4 h-4" /> Preconditions / Guardrails</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={templateForm.preconditions.require_online}
                    onCheckedChange={v => setTemplateForm(p => ({ ...p, preconditions: { ...p.preconditions, require_online: v } }))}
                    data-testid="precond-online" />
                  <span className="text-sm">Require Online</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={templateForm.preconditions.require_speed_zero}
                    onCheckedChange={v => setTemplateForm(p => ({ ...p, preconditions: { ...p.preconditions, require_speed_zero: v } }))}
                    data-testid="precond-speed" />
                  <span className="text-sm">Require Speed = 0</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={templateForm.preconditions.require_ignition_off}
                    onCheckedChange={v => setTemplateForm(p => ({ ...p, preconditions: { ...p.preconditions, require_ignition_off: v, require_ignition_on: v ? false : p.preconditions.require_ignition_on } }))} />
                  <span className="text-sm">Require Ignition OFF</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={templateForm.preconditions.require_ignition_on}
                    onCheckedChange={v => setTemplateForm(p => ({ ...p, preconditions: { ...p.preconditions, require_ignition_on: v, require_ignition_off: v ? false : p.preconditions.require_ignition_off } }))} />
                  <span className="text-sm">Require Ignition ON</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-sm whitespace-nowrap">Max Last Seen (minutes):</Label>
                <Input type="number" className="w-24"
                  value={templateForm.preconditions.max_last_seen_minutes || ''}
                  onChange={e => setTemplateForm(p => ({ ...p, preconditions: { ...p.preconditions, max_last_seen_minutes: e.target.value ? parseInt(e.target.value) : null } }))}
                  placeholder="None" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setTemplateDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={saveTemplate} disabled={loading} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white gap-2" data-testid="save-template-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════ RISK CONFIRMATION DIALOG ═══════════════════════ */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {confirmDialog?.riskLevel === 'critical' ? 'CRITICAL' : 'HIGH'} Risk Command
            </DialogTitle>
            <DialogDescription>
              You are about to send a <strong className="text-foreground">{confirmDialog?.riskLevel}</strong> risk command 
              (<strong>{confirmDialog?.templateName}</strong>) to <strong>{selectedDeviceIds.length}</strong> device(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">To confirm, type the following exactly:</p>
            <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">{confirmDialog?.expectedText}</code>
            <Input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="Type confirmation text..."
              className="font-mono"
              data-testid="risk-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setConfirmDialog(false)}>Cancel</Button>
            <Button size="sm"
              onClick={confirmAndSend}
              disabled={confirmText !== confirmDialog?.expectedText}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="risk-confirm-send-btn"
            >
              <AlertTriangle className="w-4 h-4" /> Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════ COMMAND DETAIL DIALOG ═══════════════════════ */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="w-[min(94vw,980px)] max-w-none max-h-[88vh] overflow-hidden" data-testid="command-detail-dialog">
          <DialogHeader>
            <DialogTitle>Command Details</DialogTitle>
            <DialogDescription>Full details for this command entry.</DialogDescription>
          </DialogHeader>
          {detailDialog && (
            <div className="max-h-[calc(88vh-7rem)] space-y-4 overflow-y-auto pr-2 text-sm" data-testid="command-detail-content">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="min-w-0"><span className="font-medium">ID:</span> <span className="font-mono text-xs break-all">{detailDialog.id}</span></div>
                <div className="min-w-0"><span className="font-medium">Status:</span> <Badge className={STATUS_BADGE[detailDialog.status]}>{detailDialog.status}</Badge></div>
                <div className="min-w-0"><span className="font-medium">Device:</span> <span className="break-words" data-testid="detail-device-label">{formatCommandDeviceLabel(detailDialog) !== '-' ? formatCommandDeviceLabel(detailDialog) : (detailDialog.device_imei || '-')}</span></div>
                <div className="min-w-0"><span className="font-medium">IMEI:</span> <span className="font-mono text-xs break-all">{detailDialog.device_imei || '-'}</span></div>
                <div className="min-w-0"><span className="font-medium">ICCID:</span> <span className="font-mono text-xs break-all" data-testid="detail-iccid">{detailDialog.iccid || '-'}</span></div>
                <div className="min-w-0"><span className="font-medium">Mobile:</span> <span className="break-all">{detailDialog.mobile_number || '-'}</span></div>
                <div className="min-w-0"><span className="font-medium">Method:</span> <span className="break-words">{detailDialog.delivery_method || 'gprs'}</span></div>
                <div className="min-w-0"><span className="font-medium">Created:</span> <span className="break-words">{formatDate(detailDialog.created_at)}</span></div>
                <div className="min-w-0"><span className="font-medium">Sent At:</span> <span className="break-words">{formatDate(detailDialog.sent_at || detailDialog.created_at)}</span></div>
                <div className="min-w-0" data-testid="detail-replied-at"><span className="font-medium">Replied At:</span> <span className="break-words">{detailDialog.responded_at ? formatDate(detailDialog.responded_at) : '-'}</span></div>
                <div className="min-w-0" data-testid="detail-reply-latency"><span className="font-medium">Reply Latency:</span> <span className="break-words tabular-nums">{formatReplyLatency(detailDialog)}</span></div>
                <div className="min-w-0"><span className="font-medium">Sent By:</span> <span className="break-all">{detailDialog.created_by || detailDialog.sent_by || '-'}</span></div>
                <div className="min-w-0"><span className="font-medium">Source:</span> <span className="break-words">{detailDialog.source || '-'}</span></div>
              </div>
              <div>
                <span className="font-medium">Command:</span>
                <DetailTextBox testId="command-detail-command-text">
                  {detailDialog.command || detailDialog.message || '-'}
                </DetailTextBox>
              </div>
              {detailDialog.response && (
                <div>
                  <span className="font-medium">Response:</span>
                  <div className="mt-1 max-h-48 overflow-auto rounded bg-green-50 p-2 text-xs font-mono leading-relaxed text-green-800 whitespace-pre-wrap break-all dark:bg-green-950/30 dark:text-green-300" data-testid="command-detail-response-text">
                    {linkifyText(detailDialog.response)}
                  </div>
                </div>
              )}
              {detailDialog.error && (
                <div>
                  <span className="font-medium text-red-600">Error:</span>
                  <div className="mt-1 max-h-48 overflow-auto rounded bg-red-50 p-2 text-xs font-mono leading-relaxed text-red-800 whitespace-pre-wrap break-all dark:bg-red-950/30 dark:text-red-300" data-testid="command-detail-error-text">
                    {linkifyText(detailDialog.error)}
                  </div>
                </div>
              )}
              {(detailDialog.sequence_progress?.length > 0 || detailDialog.sequence_steps) && (
                <div data-testid="detail-sequence-steps">
                  <span className="font-medium">
                    Sequence Steps
                    {detailDialog.sequence_total > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground tabular-nums" data-testid="detail-sequence-progress-count">
                        ({(detailDialog.sequence_progress || []).filter(p => p.status === 'sent').length}/{detailDialog.sequence_total} sent)
                      </span>
                    )}
                    :
                  </span>
                  <div className="mt-1 space-y-1">
                    {(detailDialog.sequence_progress?.length > 0 ? detailDialog.sequence_progress : detailDialog.sequence_steps).map((s, i) => {
                      const stepStatus = s.status; // undefined for legacy sequence_steps rows
                      const isRunning = detailDialog.status === 'sending' && stepStatus === 'pending'
                        && (detailDialog.sequence_progress || []).findIndex(p => p.status === 'pending') === i;
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs" data-testid={`detail-seq-step-${i}`}>
                          {stepStatus === 'sent' && <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" />}
                          {stepStatus === 'failed' && <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                          {stepStatus === 'skipped' && <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          {isRunning && <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin shrink-0" />}
                          {stepStatus === 'pending' && !isRunning && <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          <span className="text-muted-foreground">{i + 1}.</span>
                          <code className="bg-muted px-1 rounded">{s.command_text || s.command}</code>
                          {s.delay_ms > 0 && <span className="text-muted-foreground">({s.delay_ms}ms delay)</span>}
                          {s.sent_at && <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap">{formatDate(s.sent_at)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {detailDialog.created_by && (
                <div className="min-w-0"><span className="font-medium">Created by:</span> <span className="break-all">{detailDialog.created_by || detailDialog.sent_by}</span></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* iter151 Phase 3.5 — "View Device Status at Send Time" modal.
          Opens from the audit log "Status @ Send" column.  Shows the
          device_data packet closest to the audit entry's created_at for
          each device that was part of the action — so admins can answer
          "where was the vehicle / what was its state when this command
          went out?" without leaving the audit view. */}
      <Dialog open={!!stateAtSendDialog} onOpenChange={() => setStateAtSendDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="state-at-send-dialog">
          <DialogHeader>
            <DialogTitle>Device Status @ Send Time</DialogTitle>
          </DialogHeader>
          {stateAtSendDialog && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                Showing the closest device packet (±10&nbsp;min) to send time
                <span className="font-mono ml-1">{formatDate(stateAtSendDialog.entry?.created_at)}</span>
              </div>
              {stateAtSendDialog.loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Looking up device states…
                </div>
              )}
              {stateAtSendDialog.error && (
                <div className="text-sm text-red-700 dark:text-red-400" data-testid="state-at-send-error">{stateAtSendDialog.error}</div>
              )}
              {!stateAtSendDialog.loading && !stateAtSendDialog.error && stateAtSendDialog.devices?.length > 0 && (
                <div className="space-y-4">
                  {stateAtSendDialog.devices.map((d, idx) => (
                    <div
                      key={d.imei || idx}
                      className="rounded-lg border bg-card p-3"
                      data-testid={`state-at-send-card-${idx}`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                        <div className="font-medium text-sm" data-testid={`state-at-send-label-${idx}`}>{d.label || d.imei || 'Unknown device'}</div>
                        <div className="flex items-baseline gap-3 text-[10px] text-muted-foreground">
                          {d.command_status && (
                            <span>cmd: <span className="font-medium">{d.command_status}</span></span>
                          )}
                          {d.latency_ms !== null && d.latency_ms !== undefined && (
                            <span className="tabular-nums">latency: <span className="font-medium">{(d.latency_ms / 1000).toFixed(1)}s</span></span>
                          )}
                        </div>
                      </div>
                      {d.error && (
                        <div className="text-xs text-red-700 dark:text-red-400">{d.error}</div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* @ Send card */}
                        {(() => {
                          const s = d.state_at_send;
                          return (
                            <DeviceStatePanel
                              kind="send"
                              testIdSuffix={`send-${idx}`}
                              title="@ Send"
                              subtitle={formatDate(stateAtSendDialog.entry?.created_at)}
                              state={s}
                            />
                          );
                        })()}
                        {/* @ Ack card */}
                        {(() => {
                          const a = d.state_at_ack;
                          return (
                            <DeviceStatePanel
                              kind="ack"
                              testIdSuffix={`ack-${idx}`}
                              title="@ Ack"
                              subtitle={d.ack_time ? formatDate(d.ack_time) : 'no reply yet'}
                              state={a}
                              missingHint={d.ack_time ? null : 'Device has not acknowledged the command.'}
                            />
                          );
                        })()}
                      </div>
                      {d.command_response && (
                        <div className="mt-2 text-[11px] text-muted-foreground">
                          <span className="font-medium">Device response:</span>{' '}
                          <span className="font-mono">{d.command_response}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!stateAtSendDialog.loading && !stateAtSendDialog.error && stateAtSendDialog.devices?.length === 0 && (
                <div className="text-sm text-muted-foreground">No devices associated with this audit entry.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
